import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ORAOverlayCard from "./ORAOverlayCard";
import DrawingOverlay from "./DrawingOverlay";
import RadarPlaybackBar from "./RadarPlaybackBar";
import "./StormAtlasRadarPanel.css";

// ── Extracted modules ────────────────────────────────────────────────────────
import { haversineMeters, formatDistance, generateRangeRingGeoJson } from "../utils/gis";
import {
  normalizeUrl, toAbsoluteUrl, parseFrameTime, boundsToCoords, hasValidBounds,
  safeUpdateImage, warningsToGeoJson,
  getWarningIcon, getWarningObsType, getExpiresCountdown, clampPopupPos, parseWarnAttrs,
} from "../utils/radar-helpers";
import {
  RADAR_NETWORKS, RADAR_SITES_CATALOG,
  RADAR_PRODUCT_GROUPS, ALL_PRODUCTS, VALID_PRODUCT_IDS,
  PRODUCT_SHORT_LABELS, REFLECTIVITY_LEGEND_STOPS, VELOCITY_LEGEND_STOPS,
  buildRadarSitesGeoJson, setRadarSitesVisibility,
} from "../data/radar-sites";
import {
  TRANSPARENT_1PX, PLACEHOLDER_COORDS,
  initRadarLayers, repositionRadarBelowRoads, setTileSourceUrl, initMeasureLayers, resetMeasureLayers,
  initWarningLayers, initFrontsLayers, setFrontsVisibility,
  initMdLayers, setMdVisibility,
  initMetarLayers, setMetarVisibility, enrichMetarData,
  DARK_MAP_STYLE_IDS,
  initRadarSitesLayers, initRangeRingLayer,
  syncRadarResampling,
  initSpotterLayers, setSpotterVisibility,
  initReportLayers,  setReportVisibility, setReportFilter,
} from "../mapbox/layers";
import useMrmsManifest from "../hooks/useMrmsManifest";
import useVelocityManifest from "../hooks/useVelocityManifest";
import useRadarSiteHealth from "../hooks/useRadarSiteHealth";
import SiteSelector from "./SiteSelector";
import useTileReadiness from "../hooks/useTileReadiness";
import RadarPlaybackEngine from "../engine/RadarPlaybackEngine";
import RadarMapRenderer from "../engine/RadarMapRenderer";
import SpotterPopup from "./SpotterPopup";
import ProbeOverlay from "./ProbeOverlay";
import { sampleDbzFromTileAsync, getBearing, bearingToCardinal, beamHeightKm, findNearestMetar, haversineKm, formatProbeDistance } from "../utils/radarProbe";
import {
  MOCK_SPOTTERS, MOCK_REPORTS,
  REPORT_CONFIG, REPORT_SOURCES,
  spottersToGeoJson, reportsToGeoJson,
  fetchMPingReports,
} from "../data/spotterNetwork";
import ReportSheet from "./ReportSheet";

const API_BASE        = process.env.REACT_APP_API_BASE     ?? "/api";
const MAPBOX_TOKEN    = (process.env.REACT_APP_MAPBOX_TOKEN || "").trim();
// Adaptive playback cadence by device class.
// Mobile tiles load slower; giving each frame more display time prevents blank
// frames caused by promotions firing before tiles have arrived.
const _ua            = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const _isMobile      = /Mobi|Android/i.test(_ua);
const _isTablet      = /iPad|Tablet/i.test(_ua) && !_isMobile;
const FRAME_MS       = _isMobile ? 800 : 600;                     // MRMS cadence (ms)
const RADAR_FRAME_MS = _isMobile ? 1000 : _isTablet ? 850 : 700; // NEXRAD cadence (ms)

if (MAPBOX_TOKEN) mapboxgl.accessToken = MAPBOX_TOKEN;

// ── MD label helpers ─────────────────────────────────────────────────────────

function mdLabelCenter(geometry) {
  const ring =
    geometry?.type === "Polygon"      ? geometry.coordinates[0] :
    geometry?.type === "MultiPolygon" ? geometry.coordinates[0][0] : null;
  if (!ring?.length) return null;
  let lngSum = 0, latSum = 0;
  for (const [lng, lat] of ring) { lngSum += lng; latSum += lat; }
  return [lngSum / ring.length, latSum / ring.length];
}

function mdLabelTag({ type, watch_probability, watch_tag }) {
  if (watch_tag) return watch_tag;
  if (watch_probability != null && watch_probability >= 60) return `WATCH LIKELY \u00b7 ${watch_probability}%`;
  if (watch_probability != null && watch_probability >= 30) return `WATCH POSSIBLE \u00b7 ${watch_probability}%`;
  if (type === "flood")  return "HEAVY RAINFALL";
  if (type === "winter") return "WINTER WEATHER";
  return "MESOSCALE DISCUSSION";
}

// ── Warning settings ─────────────────────────────────────────────────────────

const WARNING_TYPES = [
  { key: "tornado_warning",             label: "Tornado Warning",             hazard_type: "tornado", defaultColor: "#f43f5e" },
  { key: "severe_thunderstorm_warning", label: "Severe Thunderstorm Warning", hazard_type: "severe",  defaultColor: "#f59e0b" },
  { key: "flash_flood_warning",         label: "Flash Flood Warning",         hazard_type: "flood",   defaultColor: "#3b82f6" },
  { key: "winter_storm_warning",        label: "Winter Storm Warning",        hazard_type: "winter",  defaultColor: "#00ccff" },
  { key: "blizzard_warning",            label: "Blizzard Warning",            hazard_type: "blizzard",defaultColor: "#7dd3fc" },
  { key: "ice_storm_warning",           label: "Ice Storm Warning",           hazard_type: "ice",     defaultColor: "#a5f3fc" },
];

const WARNING_DEFAULTS = Object.fromEntries(
  WARNING_TYPES.map((t) => [t.key, { enabled: true, color: t.defaultColor }])
);

function loadWarningSettings() {
  try {
    const saved = localStorage.getItem("sa_warning_settings");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults so new warning types get their defaults on upgrade.
      return Object.fromEntries(
        WARNING_TYPES.map((t) => [t.key, { ...WARNING_DEFAULTS[t.key], ...(parsed[t.key] ?? {}) }])
      );
    }
  } catch { /* ignore */ }
  return WARNING_DEFAULTS;
}

/** Build a Mapbox match expression mapping hazard_type → user-chosen color. */
function buildWarnColorExpr(settings) {
  const pairs = WARNING_TYPES.flatMap((t) => [t.hazard_type, settings[t.key]?.color ?? t.defaultColor]);
  return ["match", ["get", "hazard_type"], ...pairs, "#94a3b8"];
}

// ── METAR helpers ──────────────────────────────────────────────────────────
const _CARDINAL_DIRS = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
function _cardinalDir(deg) {
  if (typeof deg !== "number") return "";
  return _CARDINAL_DIRS[Math.round(deg / 22.5) % 16];
}

// ── StormAtlasRadarPanel ────────────────────────────────────────────────────

export default function StormAtlasRadarPanel({ manifest, loading, error, activeSite, frameLimit: frameLimitProp, onFrameLimitChange, onRetry, onSwitchView, onRadarSiteSelect }) {
  const mapContainerRef  = useRef(null);
  const mapRef           = useRef(null);
  const engineRef        = useRef(null);
  const activeLayerRef   = useRef("a"); // "a" | "b" — which slot is currently front
  // Failed frame URLs — skip these during playback to avoid retrying pruned frames.
  // Cleared on session reset (product/site switch) since a new manifest will have
  // fresh URLs.
  const failedFramesRef      = useRef(new Set());
  const consecutiveFailsRef  = useRef(0);
  // Bumped when a frame is added to failedFramesRef — triggers useMemo
  // recomputation of playbackFrames without converting the Set to state.
  const [failedVersion, setFailedVersion] = useState(0);
  const markFrameFailed = useCallback((url) => {
    if (!url || failedFramesRef.current.has(url)) return;
    failedFramesRef.current.add(url);
    setFailedVersion((v) => v + 1);
  }, []);
  // sessionRef: incremented on every product/site switch.  All async callbacks
  // (interval ticks, preload onloads) capture this value at the time they are
  // created and bail out when the captured value no longer matches, ensuring
  // stale work from a prior session cannot repaint after a hard reset.
  const sessionRef       = useRef(0);
  // isSwitchingRef: true from hard reset until the first crossfade of the new
  // session clears it.  Causes that first crossfade to write directly onto
  // layer-a (already at opacity 0) rather than doing the double-buffer swap,
  // eliminating any ghost from the inactive buffer slot.
  const isSwitchingRef   = useRef(false);
  // holdFiredRef: set true by the hold-gesture timer; cleared by the trailing
  // click handler so the click is suppressed after a successful hold.
  const holdFiredRef     = useRef(false);
  // isFirstLoadRef: true after a hard reset, until frames-sync calls loadFrames.
  // Prevents frames-sync from calling updateFrames with stale frames from the
  // prior session on the first render after a dataset switch.
  const isFirstLoadRef   = useRef(false);
  // usingTilesRef: true when the current session is rendering via raster-tile
  // sources rather than image sources.  Reset and opacity logic check this to
  // target the correct layer set.
  const usingTilesRef    = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // ── Map style selector ──────────────────────────────────────────────────
  const MAP_STYLES = useMemo(() => [
    { id: "dark",              label: "Dark",              url: "mapbox://styles/mapbox/dark-v11" },
    { id: "light",             label: "Light",             url: "mapbox://styles/mapbox/light-v11" },
    { id: "streets",           label: "Streets",           url: "mapbox://styles/mapbox/streets-v12" },
    { id: "outdoors",          label: "Outdoors",          url: "mapbox://styles/mapbox/outdoors-v12" },
    { id: "satellite",         label: "Satellite",         url: "mapbox://styles/mapbox/satellite-v9" },
    { id: "satellite-streets", label: "Satellite Streets", url: "mapbox://styles/mapbox/satellite-streets-v12" },
    { id: "nav-day",           label: "Navigation Day",    url: "mapbox://styles/mapbox/navigation-day-v1" },
    { id: "nav-night",         label: "Navigation Night",  url: "mapbox://styles/mapbox/navigation-night-v1" },
    { id: "standard",          label: "Standard",          url: "mapbox://styles/mapbox/standard" },
  ], []);
  const MAP_STYLE_IDS = useMemo(() => new Set(MAP_STYLES.map(s => s.id)), [MAP_STYLES]);
  const [mapStyleId, setMapStyleId] = useState(() => {
    const saved = localStorage.getItem("sa_mapStyle");
    return MAP_STYLE_IDS.has(saved) ? saved : "dark";
  });
  const mapStyleUrlRef = useRef(
    MAP_STYLES.find(s => s.id === (localStorage.getItem("sa_mapStyle") || "dark"))?.url || "mapbox://styles/mapbox/dark-v11"
  );
  useEffect(() => { localStorage.setItem("sa_mapStyle", mapStyleId); }, [mapStyleId]);

  // ── Active radar product — declared early so all effects can depend on it ──
  const [selectedProduct, setSelectedProduct] = useState(() => {
    const stored = localStorage.getItem("sa_selectedProduct");
    return VALID_PRODUCT_IDS.has(stored) ? stored : "reflectivity";
  });
  // Incremented by the hard reset effect so Effect 2 re-runs after every
  // hard reset, even when manifest hasn't changed (e.g. product switch on
  // the same site — manifest is valid but radarFramesRef was just cleared).
  const [dataEpoch, setDataEpoch] = useState(0);

  // ── Frame limit — owned by App.js; received as prop so manifest re-fetches ──
  const frameLimit    = frameLimitProp ?? 10;
  const setFrameLimit = onFrameLimitChange ?? (() => {});

  // Velocity manifest hook — activates only when velocity is selected.
  // Declared here (before Effects 1/2) so their dep arrays can reference it.
  const vel = useVelocityManifest(selectedProduct, activeSite, API_BASE);
  // Right-pane product (dual pane only).  Declared early so the rightVel
  // hook call below satisfies React's unconditional-hooks rule.
  const [rightProduct, setRightProduct] = useState(() =>
    selectedProduct === "velocity" ? "reflectivity" : "velocity"
  );
  // Second velocity hook for the right pane — no-ops when rightProduct !== "velocity".
  const rightVel = useVelocityManifest(rightProduct, activeSite, API_BASE);

  // ── Radar tile playback via RadarMapRenderer ──────────────────────────────
  // Two effects: (1) renderer lifecycle — creates the renderer and playback
  // interval once per site change; (2) manifest sync — keeps radarFramesRef
  // current from the manifest prop already polled by App.js useManifest,
  // eliminating the duplicate /radar/manifest fetch that used to fire here.
  const p4RendererRef    = useRef(null);
  const radarPlayingRef  = useRef(true);   // read by interval tick; avoid stale closure
  const radarFramesRef   = useRef([]);     // latest frame list; updated after fetch
  const radarFrameIdxRef = useRef(0);      // current position; mutated by tick
  const radarIntervalRef = useRef(null);   // interval id; stored so handlers can start/stop it
  const showFrameRef     = useRef(null);   // latest showFrame fn; set inside effect
  // Right-pane renderer state (dual pane only — null/empty when single pane).
  const rightRendererRef  = useRef(null);
  const rightFramesRef    = useRef([]);
  const rightShowFrameRef = useRef(null);
  const [radarPlaying,    setRadarPlaying]    = useState(true);
  const [radarFrameIdx,   setRadarFrameIdx]   = useState(0);
  const [radarFrameCount, setRadarFrameCount] = useState(0);
  const [radarTimestamp,  setRadarTimestamp]  = useState(null);
  // null | "loading" | "ready" | "empty" | "error" — drives NEXRAD status banner.
  // Starts null (idle) until a site is selected and manifest fetch begins.
  const [radarLoadState,  setRadarLoadState]  = useState(null);

  const handleRadarPlayPause = useCallback(() => {
    const next = !radarPlayingRef.current;
    radarPlayingRef.current = next;
    setRadarPlaying(next);
    if (next) {
      if (!radarIntervalRef.current && showFrameRef.current) {
        radarIntervalRef.current = setInterval(showFrameRef.current, RADAR_FRAME_MS);
      }
    } else {
      clearInterval(radarIntervalRef.current);
      radarIntervalRef.current = null;
    }
  }, []);

  const handleRadarSeek = useCallback((idx) => {
    const fs = radarFramesRef.current;
    if (!fs.length) return;
    const clamped = Math.max(0, Math.min(fs.length - 1, idx));
    // Pause auto-advance.
    clearInterval(radarIntervalRef.current);
    radarIntervalRef.current = null;
    radarPlayingRef.current  = false;
    setRadarPlaying(false);
    // Render the requested frame immediately.
    radarFrameIdxRef.current = clamped;
    const f = fs[clamped];
    p4RendererRef.current?.setFrame({
      scanId:          f.scan_id,
      tileUrlTemplate: f.tile_url_template,
      tileZoomRange:   f.tile_zoom_range,
    });
    // Sync right pane to the same seek position.
    rightShowFrameRef.current?.(clamped, { immediate: true });
    setRadarFrameIdx(clamped);
    setRadarTimestamp(parseFrameTime(f.scan_id));
  }, []);

  const handleRadarPrev   = useCallback(() => handleRadarSeek(radarFrameIdxRef.current - 1), [handleRadarSeek]);
  const handleRadarNext   = useCallback(() => handleRadarSeek(radarFrameIdxRef.current + 1), [handleRadarSeek]);
  const handleRadarLatest = useCallback(() => {
    const fs = radarFramesRef.current;
    if (!fs.length) return;
    handleRadarSeek(fs.length - 1);
    // Resume playback from the latest frame.
    radarPlayingRef.current = true;
    setRadarPlaying(true);
    if (!radarIntervalRef.current && showFrameRef.current) {
      radarIntervalRef.current = setInterval(showFrameRef.current, RADAR_FRAME_MS);
    }
  }, [handleRadarSeek]);
  // Effect 1 — renderer lifecycle.
  // Creates RadarMapRenderer and the playback interval on each site change.
  // Frame loading is handled by Effect 2 (reflectivity) and Effect 2b (velocity).
  // showFrame() no-ops until radarFramesRef is populated by those effects.
  // IMPORTANT: manifest is intentionally NOT in the dependency array — the 30s
  // manifest poll must not destroy/rebuild the renderer or Mapbox will discard
  // its internal tile cache, causing hundreds of redundant tile re-fetches.
  useEffect(() => {
    if (!mapLoaded) return;
    const map = mapRef.current;
    if (!map) return;

    // Reset playback state for the incoming site.
    radarPlayingRef.current  = true;
    radarFramesRef.current   = [];
    radarFrameIdxRef.current = 0;
    setRadarPlaying(true);
    setRadarFrameIdx(0);
    setRadarFrameCount(0);
    setRadarTimestamp(null);
    setRadarLoadState("loading");

    // Destroy the outgoing renderer right before adding new layers — the
    // same layer IDs (stormatlas-radar-layer-a/b) cannot coexist. Waiting
    // until this point keeps the old site's radar visible for the full
    // manifest fetch RTT instead of blanking the map immediately on switch.
    p4RendererRef.current?.destroy();

    const renderer = new RadarMapRenderer(map);
    p4RendererRef.current = renderer;

    const showFrame = ({ immediate = false } = {}) => {
      const fs = radarFramesRef.current;
      if (!fs.length) return;
      const idx = radarFrameIdxRef.current < fs.length ? radarFrameIdxRef.current : 0;
      const f = fs[idx];
      renderer.setFrame({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }, { immediate });
      // Drive right pane at the SAME index BEFORE advancing.
      rightShowFrameRef.current?.(idx, { immediate });
      setRadarFrameIdx(idx);
      setRadarTimestamp(parseFrameTime(f.scan_id));
      radarFrameIdxRef.current = (idx + 1) % fs.length;
    };

    showFrameRef.current = showFrame;
    showFrame(); // no-op until Effect 2/2b loads frames
    const intervalId = setInterval(showFrame, RADAR_FRAME_MS);
    radarIntervalRef.current = intervalId;

    return () => {
      showFrameRef.current = null;
      clearInterval(radarIntervalRef.current);
      radarIntervalRef.current = null;
      p4RendererRef.current?.destroy();
      p4RendererRef.current = null;
    };
  }, [mapLoaded, activeSite]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 — manifest sync (reflectivity / non-velocity NEXRAD only).
  // Keeps radarFramesRef current from the manifest prop (already fetched and
  // polled every 30 s by useManifest in App.js) so the rolling frame buffer
  // stays fresh without a second independent manifest request.
  // fitBounds is gated on a ref so it fires only on explicit site switch,
  // not on every 30 s poll update.
  // Velocity frames are handled by Effect 2b below; guard here prevents the
  // 30 s reflectivity poll from overwriting velocity frames mid-session.
  const lastFitSiteRef = useRef(null);
  useEffect(() => {
    if (!mapLoaded) return;
    if (selectedProduct === "velocity" || selectedProduct === "mrms") return;
    const frames = manifest?.frames;
    if (!frames?.length) return;
    const map = mapRef.current;
    if (!map) return;

    // Apply user-selected frame limit — take the most-recent N frames.
    const limited = frames.slice(-frameLimit);

    // On first frame arrival for this site jump to the newest frame,
    // matching the original behaviour of starting at the latest scan.
    if (radarFramesRef.current.length === 0) {
      radarFrameIdxRef.current = limited.length - 1;
    } else if (radarFrameIdxRef.current >= limited.length) {
      radarFrameIdxRef.current = limited.length - 1;
      setRadarFrameIdx(limited.length - 1);
    }

    // Pre-normalize tile URLs so showFrame() can use them directly.
    radarFramesRef.current = limited.map(f => ({
      ...f,
      tile_url_template: toAbsoluteUrl(normalizeUrl(f.tile_url_template)),
    }));
    // Only trigger React rerenders when values actually change — this effect
    // runs every 30s on manifest poll but frame count rarely changes.
    setRadarFrameCount(prev => prev === limited.length ? prev : limited.length);
    setRadarLoadState(prev => prev === "ready" ? prev : "ready");

    // Show the latest frame FIRST — its slot is lazily created by setFrame(),
    // so Mapbox starts loading only this frame's tiles immediately.  Skipping
    // the 200ms crossfade (immediate: true) gets the first pixel on screen
    // as fast as possible after the manifest arrives.
    if (showFrameRef.current) showFrameRef.current({ immediate: true });

    // Then batch-create remaining frame sources for background playback.
    // The latest frame's slot already exists (idempotent), so it's skipped.
    // Remaining frames' tiles load in the background without competing with
    // the first frame's tiles for bandwidth.
    p4RendererRef.current?.setFrames(
      radarFramesRef.current.map(f => ({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }))
    );

    // Fit map to site bounds only on an explicit site switch.
    const bounds = frames[0]?.bounds;
    if (bounds && activeSite && manifest?.site === activeSite && activeSite !== lastFitSiteRef.current) {
      lastFitSiteRef.current = activeSite;
      if (window.matchMedia('(pointer: coarse)').matches) {
        // Mobile: preserve zoom, only pan if site center is off-screen
        const siteLon = (bounds.west + bounds.east) / 2;
        const siteLat = (bounds.south + bounds.north) / 2;
        if (!map.getBounds().contains([siteLon, siteLat])) {
          map.easeTo({ center: [siteLon, siteLat], duration: 400 });
        }
      } else {
        map.fitBounds(
          [[bounds.west, bounds.south], [bounds.east, bounds.north]],
          { padding: 40, duration: 800 },
        );
        // Defensive: after the 800ms fitBounds animation, snap the right map
        // to the left map's final camera to eliminate any residual drift.
        if (rightMapRef.current) {
          setTimeout(() => {
            const lm = mapRef.current;
            const rm = rightMapRef.current;
            if (lm && rm) {
              rm.jumpTo({
                center:  lm.getCenter(),
                zoom:    lm.getZoom(),
                bearing: lm.getBearing(),
                pitch:   lm.getPitch(),
              });
            }
          }, 850);
        }
      }
    }
  }, [mapLoaded, manifest, activeSite, selectedProduct, dataEpoch, frameLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2b — velocity manifest sync.
  // Parallel to Effect 2: populates radarFramesRef from the velocity manifest
  // when the velocity product is active.  No fitBounds — product switches do
  // not recenter the map; only site switches do.
  useEffect(() => {
    if (!mapLoaded || selectedProduct !== "velocity") return;
    const frames = vel.manifest?.frames;
    if (!frames?.length) {
      // Velocity manifest has no frames yet — clear stale frames and show zero count.
      radarFramesRef.current = [];
      setRadarFrameCount(0);
      setRadarLoadState("loading");
      return;
    }
    const limited = frames.slice(-frameLimit);
    if (radarFramesRef.current.length === 0) {
      radarFrameIdxRef.current = limited.length - 1;
    } else if (radarFrameIdxRef.current >= limited.length) {
      radarFrameIdxRef.current = limited.length - 1;
      setRadarFrameIdx(limited.length - 1);
    }
    // Pre-normalize tile URLs so showFrame() can use them directly,
    // matching the normalization in Effects 1 and 2 for reflectivity.
    radarFramesRef.current = limited.map(f => ({
      ...f,
      tile_url_template: toAbsoluteUrl(normalizeUrl(f.tile_url_template)),
    }));
    setRadarFrameCount(limited.length);
    setRadarLoadState("ready");

    // Show latest velocity frame immediately (same priority strategy as Effect 2).
    if (showFrameRef.current) showFrameRef.current({ immediate: true });

    // Warm remaining velocity frame sources in background.
    p4RendererRef.current?.setFrames(
      radarFramesRef.current.map(f => ({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }))
    );
  }, [mapLoaded, vel.manifest, selectedProduct, activeSite, frameLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tile readiness gating ──────────────────────────────────────────────
  // Tracks whether the back-buffer tile source has finished loading its
  // visible tiles.  The crossfade is held until ready (or timeout).
  const tileReadiness = useTileReadiness(mapRef.current, mapLoaded);
  // pendingPromotion: stashed closure that performs the opacity swap.
  // Set by the crossfade effect when tiles aren't ready yet; executed by
  // the readiness watcher or timeout.
  const pendingPromotionRef = useRef(null);
  const promotionTimerRef   = useRef(null);
  // Must stay below FRAME_MS so the timeout fires before the next frame tick.
  const TILE_READY_TIMEOUT  = _isMobile ? 600 : _isTablet ? 500 : 400; // ms
  // lastAppliedScanId: prevents re-applying the same frame when currentFrame
  // reference changes but the underlying scan is identical (e.g. manifest poll).
  const lastAppliedScanIdRef = useRef(null);

  // ── Radar opacity ────────────────────────────────────────────────────────
  const radarOpacity    = 0.85;
  const radarOpacityRef = useRef(0.85); // ref so crossfade effect reads latest without re-running


  // ── Radar products menu ──────────────────────────────────────────────────
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [isFramesMenuOpen,   setIsFramesMenuOpen]   = useState(false);
  // selectedProduct / setSelectedProduct declared near top of component (before effects).
  // frameLimit / setFrameLimit declared near top of component (before effects).

  const selectedProductMeta       = ALL_PRODUCTS.find((item) => item.id === selectedProduct) || ALL_PRODUCTS[0];
  const selectedProductLabel      = selectedProductMeta?.label || "Hi-Res Reflectivity";
  const selectedProductShortLabel = PRODUCT_SHORT_LABELS[selectedProduct] || "REF";

  // ── Map tool mode ─────────────────────────────────────────────────────────
  // null | "measure" | "dbz" | "draw" — only one active at a time.
  const [activeMapTool, setActiveMapTool]       = useState(null);
  const isMeasureMode = activeMapTool === "measure"; // derived, backwards-compat
  const isDrawMode    = activeMapTool === "draw";

  // ── Drawing tool ──────────────────────────────────────────────────────────
  const [drawColor, setDrawColor] = useState("#ef4444"); // red default
  const drawingOverlayRef         = useRef(null);

  // ── Ruler / distance measurement ─────────────────────────────────────────
  const [measurePoints, setMeasurePoints]       = useState([]);
  const [measurementUnit, setMeasurementUnit]   = useState(
    () => localStorage.getItem("stormatlas.ruler.units") ?? "miles"
  );
  const [measureLabelPx, setMeasureLabelPx]     = useState(null);
  const [, setIsMeasureUnitOpen] = useState(false);
  const [measureFinalized, setMeasureFinalized] = useState(false);
  const measureFinalizedRef = useRef(false);
  useEffect(() => { measureFinalizedRef.current = measureFinalized; }, [measureFinalized]);
  const isMeasureModeRef = useRef(false);
  // isMeasureModeRef: true whenever ANY interactive map tool is active (blocks site/warning clicks)
  useEffect(() => { isMeasureModeRef.current = activeMapTool !== null; }, [activeMapTool]);
  const activeMapToolRef = useRef(null);
  useEffect(() => { activeMapToolRef.current = activeMapTool; }, [activeMapTool]);

  // ── Long-press site switch toast ─────────────────────────────────────────
  const [siteSwitchToast, setSiteSwitchToast] = useState(null); // string | null

  // ── dBZ probe ─────────────────────────────────────────────────────────────
  const [dbzProbeInfo, setDbzProbeInfo]         = useState(null); // { x, y, lng, lat } | null
  // Detect touch-only devices once — drives mobile probe vs desktop tooltip.
  const isTouchDevice = useRef(typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches).current;
  // Per-user unit prefs for the dBZ probe — persisted to localStorage.
  const [dbzDistUnit, setDbzDistUnit] = useState(() => localStorage.getItem('stormatlas.dbz.distUnit') || 'km');
  useEffect(() => { localStorage.setItem('stormatlas.dbz.distUnit', dbzDistUnit); }, [dbzDistUnit]);
  const [dbzAltUnit,  setDbzAltUnit]  = useState(() => localStorage.getItem('stormatlas.dbz.altUnit')  || 'km');
  useEffect(() => { localStorage.setItem('stormatlas.dbz.altUnit',  dbzAltUnit);  }, [dbzAltUnit]);
  const [isDbzSettingsOpen, setIsDbzSettingsOpen] = useState(false);
  // Full site object for the currently active ICAO — used by ProbeOverlay for distance/bearing.
  const activeSiteData = useMemo(
    () => activeSite ? (RADAR_SITES_CATALOG.find(s => s.id === activeSite) ?? null) : null,
    [activeSite],
  );
  // Ref so the stable onMove closure can read the latest site without
  // re-registering the mousemove handler on every site change.
  const probeActiveSiteRef = useRef(null);
  useEffect(() => { probeActiveSiteRef.current = activeSiteData; }, [activeSiteData]);
  // Absolute tile URL for the currently displayed frame — updated whenever
  // currentFrame changes so the dBZ probe always reads from the right tile.
  const probeTileUrlRef = useRef(null);
  // probeMetarDataRef and probeMetarHystRef are declared after metarData below.

  // ── Dual pane mode ────────────────────────────────────────────────────────
  const [isDualPane, setIsDualPane]     = useState(false);
  const [activePane, setActivePane]     = useState("left"); // "left" | "right"
  const rightMapContainerRef            = useRef(null);
  const rightMapRef                     = useRef(null);
  const [rightMapLoaded, setRightMapLoaded] = useState(false);
  const isSyncingRef                    = useRef(false); // prevents infinite move-sync loops
  // rightProduct is declared near line 100 (before rightVel hook).
  // Resize both maps when toggling dual pane — Mapbox needs to know its
  // container changed size, otherwise tiles render at the old dimensions.
  // Snapshot the camera BEFORE resize (canonical truth the user was viewing
  // in single-pane mode), then after the browser finishes the flex-layout
  // reflow apply that exact camera to BOTH maps so the target is centered
  // identically in each pane.
  useEffect(() => {
    const left = mapRef.current;
    // Capture the pre-resize camera — this is what the user was looking at.
    const cam = left ? {
      center:  left.getCenter(),
      zoom:    left.getZoom(),
      bearing: left.getBearing(),
      pitch:   left.getPitch(),
    } : null;

    const raf = requestAnimationFrame(() => {
      const right = rightMapRef.current;
      left?.resize();
      right?.resize();
      // Force both maps to the same canonical camera after resize.
      if (cam) {
        left?.jumpTo(cam);
        right?.jumpTo(cam);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isDualPane]);

  // In dual-pane mode the product chip/dropdown controls the *active* pane.
  const activePaneProduct      = isDualPane && activePane === "right" ? rightProduct : selectedProduct;
  const activePaneMeta         = ALL_PRODUCTS.find((p) => p.id === activePaneProduct) || ALL_PRODUCTS[0];
  const activePaneLabel        = activePaneMeta?.label || "Hi-Res Reflectivity";
  const activePaneShortLabel   = PRODUCT_SHORT_LABELS[activePaneProduct] || "REF";

  // ── Radar sites visibility ────────────────────────────────────────────────
  const [radarSitesVisible, setRadarSitesVisible] = useState(true);

  // ── Radar site health ─────────────────────────────────────────────────────
  const { siteHealthMap, siteHealthDetails } = useRadarSiteHealth(API_BASE);
  const [siteSelectorOpen, setSiteSelectorOpen] = useState(false);

  // ── Settings dropdown ────────────────────────────────────────────────────
  const [isSettingsOpen,         setIsSettingsOpen]         = useState(false);
  const [isInfoOpen,             setIsInfoOpen]             = useState(false);
  // Single accordion state — only one top-level section open at a time.
  // Values: "layers" | "tools" | "ora" | "app" | "account" | null
  const [openSection,            setOpenSection]            = useState(null);
  const toggleSection = (name) => setOpenSection((cur) => cur === name ? null : name);
  // Nested sub-section toggles (independent of accordion)
  const [isMesoscaleOpen,        setIsMesoscaleOpen]        = useState(false);
  const [isMrmsOpen,             setIsMrmsOpen]             = useState(false);
  const [isFrontsOpen,           setIsFrontsOpen]           = useState(false);
  const [isPlaybackSettingsOpen, setIsPlaybackSettingsOpen] = useState(false);
  const [isChaserNetworkOpen,    setIsChaserNetworkOpen]    = useState(false);
  const [isMapStyleOpen,         setIsMapStyleOpen]         = useState(false);

  // ── Fronts overlay ──────────────────────────────────────────────────────
  const [showFronts, setShowFronts] = useState(
    () => localStorage.getItem("sa_showFronts") === "1"
  );
  const [frontsData,  setFrontsData]  = useState(null);
  const [frontsError, setFrontsError] = useState(false);
  const showFrontsRef = useRef(false);
  useEffect(() => { showFrontsRef.current = showFronts; }, [showFronts]);

  // ── Mesoscale Discussions overlay ────────────────────────────────────────
  const [mdEnabled,  setMdEnabled]  = useState(true);
  const [mdFilters,  setMdFilters]  = useState({ severe: true, flood: true, winter: false });
  const [mdData,     setMdData]     = useState(null);
  const [selectedMD, setSelectedMD] = useState(null);
  const [mdPopupPos, setMdPopupPos] = useState(null);
  const mdMarkersRef  = useRef([]);
  // Ref so the stable style.load closure can restore MD visibility without
  // re-registering the handler on every toggle (mirrors metarEnabledRef pattern).
  const mdEnabledRef  = useRef(true);
  useEffect(() => { mdEnabledRef.current = mdEnabled; }, [mdEnabled]);

  // ── METAR overlay ──────────────────────────────────────────────────────────
  const [metarEnabled,  setMetarEnabled]  = useState(false);
  const [metarData,     setMetarData]     = useState(null);
  // Probe refs declared here so metarData is in scope (ESLint no-use-before-define).
  const probeMetarDataRef = useRef(null);
  useEffect(() => { probeMetarDataRef.current = metarData; }, [metarData]);
  const probeMetarHystRef = useRef(null); // METAR hysteresis for desktop probe
  const [selectedMetar, setSelectedMetar] = useState(null);
  const [metarPopupPos,    setMetarPopupPos]    = useState(null);
  // Ref so the stable style.load closure can restore METAR visibility without
  // re-registering the handler on every toggle (mirrors showFrontsRef pattern).
  const metarEnabledRef   = useRef(false);
  useEffect(() => { metarEnabledRef.current = metarEnabled; }, [metarEnabled]);
  const windBarbColorRef  = useRef(DARK_MAP_STYLE_IDS.has(mapStyleId) ? "#ffffff" : "#000000");
  useEffect(() => { windBarbColorRef.current = DARK_MAP_STYLE_IDS.has(mapStyleId) ? "#ffffff" : "#000000"; }, [mapStyleId]);

  // ── Spotter Network ──────────────────────────────────────────────────────────
  const [spotterEnabled,  setSpotterEnabled]  = useState(false);
  const [reportsEnabled,  setReportsEnabled]  = useState(false);
  const [reportSources,   setReportSources]   = useState(["mping", "spotter", "stormatlas"]);
  const [reportTypes,     setReportTypes]     = useState(["tornado", "funnel", "hail", "wind", "downburst", "flood", "rain"]);
  const [mpingReports,    setMpingReports]    = useState([]);
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const [selectedSpotter, setSelectedSpotter] = useState(null);
  const [selectedReport,  setSelectedReport]  = useState(null);
  const [spotterPopupPos, setSpotterPopupPos] = useState(null);
  const spotterEnabledRef = useRef(false);
  const reportsEnabledRef = useRef(false);
  useEffect(() => { spotterEnabledRef.current = spotterEnabled; }, [spotterEnabled]);
  useEffect(() => { reportsEnabledRef.current = reportsEnabled; }, [reportsEnabled]);

  // Persisted UI preferences
  const [rulerBehavior, setRulerBehavior] = useState(
    () => localStorage.getItem("sa_rulerBehavior") ?? "ask"
  );

  useEffect(() => {
    localStorage.setItem("sa_selectedProduct", selectedProduct);
  }, [selectedProduct]);

  function handleRulerUnitChange(val) {
    setMeasurementUnit(val);
    localStorage.setItem("stormatlas.ruler.units", val);
  }
  function handleRulerBehaviorChange(val) {
    setRulerBehavior(val);
    localStorage.setItem("sa_rulerBehavior", val);
  }

  // ── Map style change handler ───────────────────────────────────────────
  // setStyle() destroys all custom sources/layers.  Cycling mapLoaded
  // false → true (via the style.load handler) triggers all effect cleanups
  // and re-initializations automatically.
  const handleMapStyleChange = useCallback((styleId) => {
    const style = MAP_STYLES.find(s => s.id === styleId);
    if (!style || styleId === mapStyleId) return;
    setMapLoaded(false);
    if (rightMapRef.current) setRightMapLoaded(false);
    setMapStyleId(styleId);
    mapStyleUrlRef.current  = style.url;
    windBarbColorRef.current = DARK_MAP_STYLE_IDS.has(styleId) ? "#ffffff" : "#000000";
    mapRef.current?.setStyle(style.url);
    rightMapRef.current?.setStyle(style.url);
  }, [mapStyleId, MAP_STYLES]);

  // ── Selected radar site + range ring ─────────────────────────────────────
  const [selectedRadarId, setSelectedRadarId] = useState(null);

  // Refs so the stable Mapbox click-handler closure can read current product
  // and selection without needing to re-register listeners on every change.
  const selectedProductRef = useRef(selectedProduct);
  const selectedRadarIdRef = useRef(null);
  useEffect(() => { selectedProductRef.current = selectedProduct; }, [selectedProduct]);
  useEffect(() => { selectedRadarIdRef.current = selectedRadarId; }, [selectedRadarId]);

  // ── Warning polygons ──────────────────────────────────────────────────────
  const [activeWarnings, setActiveWarnings]       = useState([]);
  const [selectedWarningId, setSelectedWarningId] = useState(null);
  // Ref so click-handler closures always see the current warnings list
  // without needing to re-register on every poll update.
  const activeWarningsRef = useRef([]);
  useEffect(() => { activeWarningsRef.current = activeWarnings; }, [activeWarnings]);

  const [selectedWarning,  setSelectedWarning]  = useState(null);
  const [warningPopupPos,  setWarningPopupPos]  = useState(null);
  const [warningExpanded,  setWarningExpanded]  = useState(false);
  const [warningNow,       setWarningNow]       = useState(() => Date.now());
  const [selectedWarnings, setSelectedWarnings] = useState([]);  // mobile sheet
  const [warnSheetOpen,    setWarnSheetOpen]    = useState(false);

  // ── Per-type warning settings ─────────────────────────────────────────────
  const [warningSettings, setWarningSettings] = useState(loadWarningSettings);
  const [isWarningsOpen,  setIsWarningsOpen]  = useState(false);
  const [expandedWarning, setExpandedWarning] = useState(null);

  // Persist to localStorage whenever settings change.
  useEffect(() => {
    localStorage.setItem("sa_warning_settings", JSON.stringify(warningSettings));
  }, [warningSettings]);

  // Refresh countdown every 30 s while a warning card is open.
  useEffect(() => {
    if (!selectedWarning) return;
    const id = setInterval(() => setWarningNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [selectedWarning]);

  // ── MRMS manifest: independent poll when MRMS product is selected ────────
  const onMrmsActivate = useCallback(() => {
    setLiveMode(true);                    // frames-sync will jumpToLatest on next poll
    engineRef.current?.jumpToLatest();    // jump immediately if engine already has frames
    engineRef.current?.play();            // resume playback
  }, []);
  const mrms = useMrmsManifest(selectedProduct, API_BASE, onMrmsActivate);
  // vel (useVelocityManifest) is declared near top of component before effects.
  const mrmsManifest = mrms.manifest;
  const mrmsError    = mrms.error;
  const mrmsLoading  = mrms.loading;
  const retryMrms    = mrms.retry;

  // Active manifest: MRMS, velocity, or the site-specific NEXRAD prop from App.js.
  const activeManifest = selectedProduct === "mrms"
    ? mrmsManifest
    : selectedProduct === "velocity"
      ? vel.manifest
      : manifest;

  // Stable radar dataset identity key — drives the hard reset session boundary.
  // selectedRadarId is a UI-only range-ring selection; it does NOT drive manifest
  // fetching and must not be used as a session key.  The real identity is:
  //   "mrms"                    — MRMS CONUS mosaic
  //   "nexrad:<site>:<product>" — NEXRAD keyed on site + product so switching
  //                               products for the same site still triggers a
  //                               hard reset and clears the prior product's tiles.
  const radarDatasetKey = selectedProduct === "mrms"
    ? "mrms"
    : `nexrad:${activeSite ?? ""}:${selectedProduct}`;

  // ── Frames ──────────────────────────────────────────────────────────────
  // Manifest frames are objects: { url, bounds }
  // Normalise URL and support legacy string format for backwards compat.
  const frames = useMemo(() => {
    const raw = activeManifest?.frames ?? [];
    return raw
      .slice()
      .sort((a, b) => {
        const ua = typeof a === "string" ? a : (a.scan_id ?? a.url ?? "");
        const ub = typeof b === "string" ? b : (b.scan_id ?? b.url ?? "");
        return ua < ub ? -1 : ua > ub ? 1 : 0;
      })
      .map((f) => {
        if (typeof f === "string") return { url: normalizeUrl(f), bounds: null };
        return {
          url: normalizeUrl(f.url),
          bounds: f.bounds ?? null,
          scanId: f.scan_id ?? null,
          tileUrlTemplate: f.tile_url_template ?? null,
          tileZoomRange: f.tile_zoom_range ?? null,
        };
      });
  }, [activeManifest]);

  // playbackFrames: frames with known-failed URLs removed.
  // Falls back to full frames list if every frame has failed (keeps last good
  // frame visible rather than showing nothing).
  const playbackFrames = useMemo(() => {
    const valid = frames.filter((f) => !failedFramesRef.current.has(f.url));
    return valid.length > 0 ? valid : frames;
  }, [frames, failedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const frameCount = playbackFrames.length;

  // Site feed freshness: "online" < 5 min · "stale" 5–15 min · "offline" > 15 min.
  const [statusTick, setStatusTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      setStatusTick(Date.now());
    }, 60000);
    return () => clearInterval(t);
  }, []);
  const siteStatus = (() => {
    void statusTick;
    // No manifest yet — backend is still warming up
    if (!activeManifest) return "initializing";
    // Manifest exists but no timestamp — if frames arrived treat as stale,
    // otherwise still initializing (first ingest hasn't completed)
    if (!activeManifest.last_success_at) {
      return frameCount > 0 ? "stale" : "initializing";
    }
    const ts = new Date(activeManifest.last_success_at).getTime();
    if (!ts) return frameCount > 0 ? "stale" : "initializing";
    const ageMs = Date.now() - ts;
    if (ageMs < 5 * 60 * 1000) return "online";
    if (ageMs < 15 * 60 * 1000) return "stale";
    return "offline";
  })();

  const [frameIndex, setFrameIndex] = useState(0);
  const [playing, setPlaying]       = useState(true);
  const [liveMode, setLiveMode]     = useState(true);

  // liveModeRef: ref mirror of liveMode so frames-sync effect can read the
  // current value without taking liveMode as a dependency (stale closure guard).
  const liveModeRef = useRef(liveMode);
  useEffect(() => { liveModeRef.current = liveMode; }, [liveMode]);

  // ── RadarPlaybackEngine lifecycle ─────────────────────────────────────────
  // Created once on mount; never recreated on site/product changes.  Session
  // boundaries are handled by engine.loadFrames([]) in the hard reset effect
  // and engine.loadFrames(frames) in the frames-sync effect below.
  useEffect(() => {
    const engine = new RadarPlaybackEngine({ frameIntervalMs: FRAME_MS });
    engineRef.current = engine;
    const unsubFrame = engine.on('frameChange', ({ index }) => {
      setFrameIndex(index); // always sync — no pendingPromotionRef guard
    });
    const unsubState = engine.on('stateChange', ({ playing: p }) => {
      setPlaying(p);
    });
    return () => {
      unsubFrame();
      unsubState();
      engine.destroy();
      engineRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlayPause = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.isPlaying ? engine.pause() : engine.play();
  }, []);

  // Spacebar → play/pause; skip when focus is in a text input.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      handlePlayPause();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePlayPause]);

  // Any manual step → review mode.  Engine clamps; no modulo wrap.
  const handlePrev = useCallback(() => {
    setLiveMode(false);
    engineRef.current?.prev(); // calls pause() + seek() internally
  }, []);
  const handleNext = useCallback(() => {
    setLiveMode(false);
    engineRef.current?.next(); // calls pause() + seek() internally
  }, []);

  // Drag to last frame → back to live; anywhere else → review.
  const handleSeek = useCallback((idx) => {
    setLiveMode(idx === frameCount - 1);
    engineRef.current?.pause();
    engineRef.current?.seek(idx);
  }, [frameCount]);

  // Latest button: jump to newest frame and re-enter live mode.
  const handleLatest = useCallback(() => {
    if (frameCount === 0) return;
    setLiveMode(true);
    engineRef.current?.jumpToLatest();
    engineRef.current?.play();
  }, [frameCount]);

  const handleWarningFocus = useCallback((warning) => {
    // Highlight the polygon for this warning (clears if warning has no id).
    setSelectedWarningId(warning?.id ?? null);

    // Show the warning detail card (same data that onWarnClick builds).
    const fullText = [warning.headline, warning.description, warning.instruction]
      .filter(Boolean).join("\n\n");
    setSelectedWarning({
      ...warning,
      fullText,
      obsType: getWarningObsType(fullText),
    });
    // Position the card near the upper-center of the map container since
    // we don't have a map-click point from the ORA list.
    const container = mapContainerRef.current;
    const cW = container?.offsetWidth  ?? window.innerWidth;
    const cH = container?.offsetHeight ?? window.innerHeight;
    setWarningPopupPos(clampPopupPos(cW / 2, cH / 3, cW, cH));
    setWarningExpanded(false);
    setWarningNow(Date.now());

    const map = mapRef.current;
    if (!map) return;
    let center = null;
    if (typeof warning.lat === "number" && typeof warning.lon === "number") {
      center = [warning.lon, warning.lat];
    } else if (warning.geometry?.type === "Polygon" && warning.geometry.coordinates?.[0]?.length) {
      const ring = warning.geometry.coordinates[0];
      center = [ring.reduce((s, c) => s + c[0], 0) / ring.length, ring.reduce((s, c) => s + c[1], 0) / ring.length];
    } else if (warning.geometry?.type === "MultiPolygon" && warning.geometry.coordinates?.[0]?.[0]?.length) {
      const ring = warning.geometry.coordinates[0][0];
      center = [ring.reduce((s, c) => s + c[0], 0) / ring.length, ring.reduce((s, c) => s + c[1], 0) / ring.length];
    } else if (warning.centroid) {
      const c = warning.centroid;
      center = [c.lon ?? c[0], c.lat ?? c[1]];
    }
    if (center) {
      map.flyTo({ center, zoom: Math.max(map.getZoom(), 7), duration: 1200, essential: true });
      // Search full catalog — not limited to NEXRAD — so international warnings
      // resolve to the nearest available radar regardless of network.
      let bestId = null, bestD = Infinity;
      for (const s of RADAR_SITES_CATALOG) {
        if (!s.active) continue;
        const d = haversineMeters(center, [s.lon, s.lat]);
        if (d < bestD) { bestD = d; bestId = s.id; }
      }
      if (bestId) setSelectedRadarId(bestId);
    }
  }, []);

  const nearbyCenter = useMemo(() => {
    if (!selectedRadarId) return null;
    const site = RADAR_SITES_CATALOG.find((s) => s.id === selectedRadarId);
    if (!site) return null;
    return { lat: site.lat, lon: site.lon, label: site.id, source: "radar" };
  }, [selectedRadarId]);

  // Keyboard shortcuts bubble up to the section
  const handleSectionKey = useCallback((e) => {
    if (e.defaultPrevented) return;
    if (frameCount < 1) return;
    if (e.key === " " && e.target.tagName === "BUTTON") return;
    switch (e.key) {
      case " ":          e.preventDefault(); handlePlayPause(); break;
      case "ArrowLeft":  e.preventDefault(); handlePrev();      break;
      case "ArrowRight": e.preventDefault(); handleNext();      break;
      default: break;
    }
  }, [frameCount, handlePlayPause, handlePrev, handleNext]);

  // ── Mapbox init ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container:             mapContainerRef.current,
      style:                 mapStyleUrlRef.current,
      center:                [-96.0, 38.5],
      zoom:                  4,
      projection:            "mercator",
      attributionControl:    false,
      preserveDrawingBuffer: true,
    });

    map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      "bottom-right",
    );

    map.on("style.load", () => {
      initRadarLayers(map);
      initMeasureLayers(map);
      initWarningLayers(map); // inserts before measure-line-glow → above radar, below measure tool
      initMdLayers(map);      // inserts before warnings-fill → above radar, below warnings
      initFrontsLayers(map);
      setFrontsVisibility(map, showFrontsRef.current);
      initRadarSitesLayers(map); // default visible=true matches radarSitesVisible initial state
      initRangeRingLayer(map);
      initMetarLayers(map, metarEnabledRef.current, windBarbColorRef.current);
      setMetarVisibility(map, metarEnabledRef.current);
      initSpotterLayers(map, spotterEnabledRef.current);
      initReportLayers(map,  reportsEnabledRef.current);
      // After all layers are initialised, move radar below roads/labels.
      repositionRadarBelowRoads(map);
      setMapLoaded(true);
    });

    // Track image-source fetch/decode errors.  These happen when a rolling-
    // buffer frame is pruned between the manifest poll and the crossfade.
    // Failed URLs are recorded so the crossfade can skip them on the next tick.
    //
    // Tile sources (radar-tiles-*) always generate 404s for empty coverage
    // areas — those are expected and should not be counted as failures.
    map.on("error", (e) => {
      if (e.error?.message?.includes("Could not load image")) {
        // Mapbox GL sets sourceId inconsistently on tile vs image errors.
        // e.sourceId is set for image sources; e.source?.id covers tile sources.
        const sid = e.sourceId || e.source?.id || "";

        // Tile sources: coverage-gap 404s / empty-tile responses are normal.
        if (sid.startsWith("radar-tiles-")) return;

        // RadarMapRenderer raster-tile sources: tile-level load errors are
        // expected during frame transitions and for out-of-range tiles.
        if (sid.startsWith("stormatlas-radar-src-")) return;

        // Image sources in tile mode: the image buffers hold only the
        // transparent placeholder and never load real URLs — suppress noise.
        if (sid.startsWith("radar-frame-") && usingTilesRef.current) return;

        // Placeholder blob URL — ignore decode errors from the transparent 1×1
        // PNG used to initialise / reset image sources between site switches.
        const frameUrl = e.source?.url;
        if (frameUrl && (frameUrl.startsWith("blob:") || frameUrl.startsWith("data:image"))) return;

        consecutiveFailsRef.current += 1;

        // Use the actual image URL (not the source ID) so markFrameFailed can
        // match it against playbackFrames[i].url and skip the broken frame.
        if (frameUrl) markFrameFailed(frameUrl);

        if (consecutiveFailsRef.current <= 1 || consecutiveFailsRef.current % 10 === 0) {
          console.warn(
            "[radar] frame load failed (" + consecutiveFailsRef.current + "x):",
            e.error.message,
            "| sourceId:", sid,
            "| source?.id:", e.source?.id,
            "| source?.url:", e.source?.url,
          );
        }
      }
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, [markFrameFailed]);

  // ── Right map lifecycle (dual pane) ──────────────────────────────────────
  // Creates / destroys the second Mapbox map when isDualPane toggles.
  // Copies the left map's camera state so both panes start at the same view.
  useEffect(() => {
    if (!isDualPane) {
      // Exiting dual mode — destroy the right map.
      if (rightMapRef.current) {
        rightMapRef.current.remove();
        rightMapRef.current = null;
        setRightMapLoaded(false);
      }
      return;
    }
    // Wait for the container to be in the DOM.
    if (!rightMapContainerRef.current) return;
    // Don't re-create if it already exists.
    if (rightMapRef.current) return;

    const left = mapRef.current;
    const rightMap = new mapboxgl.Map({
      container:             rightMapContainerRef.current,
      style:                 mapStyleUrlRef.current,
      center:                left ? left.getCenter() : [-96.0, 38.5],
      zoom:                  left ? left.getZoom() : 4,
      bearing:               left ? left.getBearing() : 0,
      pitch:                 left ? left.getPitch() : 0,
      projection:            "mercator",
      attributionControl:    false,
      preserveDrawingBuffer: true,
    });

    rightMap.on("style.load", () => {
      initFrontsLayers(rightMap);
      setFrontsVisibility(rightMap, showFrontsRef.current);
      initRadarSitesLayers(rightMap);
      initRangeRingLayer(rightMap);
      initMetarLayers(rightMap, metarEnabledRef.current, windBarbColorRef.current);
      setMetarVisibility(rightMap, metarEnabledRef.current);
      initSpotterLayers(rightMap, spotterEnabledRef.current);
      initReportLayers(rightMap,  reportsEnabledRef.current);
      initMdLayers(rightMap);
      setMdVisibility(rightMap, mdEnabledRef.current);
      repositionRadarBelowRoads(rightMap);
      setRightMapLoaded(true);
    });

    // Suppress expected tile errors on the right map.
    rightMap.on("error", (e) => {
      if (e.error?.message?.includes("Could not load image")) {
        const sid = e.sourceId || e.source?.id || "";
        if (sid.startsWith("stormatlas-radar-src-")) return;
        if (sid.startsWith("radar-tiles-")) return;
      }
    });

    rightMapRef.current = rightMap;

    return () => {
      rightMap.remove();
      rightMapRef.current = null;
      setRightMapLoaded(false);
    };
  }, [isDualPane]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Camera sync between left and right maps ─────────────────────────────
  // Uses the 'move' event for real-time sync during drag/zoom (not moveend).
  // The isSyncingRef guard prevents infinite loops: left move → jumpTo right
  // → right fires move → guard stops recursion.
  useEffect(() => {
    const left  = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !rightMapLoaded || !left || !right) return;

    const syncRight = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      right.jumpTo({
        center:  left.getCenter(),
        zoom:    left.getZoom(),
        bearing: left.getBearing(),
        pitch:   left.getPitch(),
      });
      isSyncingRef.current = false;
    };

    const syncLeft = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      left.jumpTo({
        center:  right.getCenter(),
        zoom:    right.getZoom(),
        bearing: right.getBearing(),
        pitch:   right.getPitch(),
      });
      isSyncingRef.current = false;
    };

    left.on('move', syncRight);
    right.on('move', syncLeft);

    // Initial sync: right map may have been created slightly before the left
    // map finished its fitBounds animation — snap to left's current camera.
    syncRight();

    // Pane selection — the .rp-pane shell has pointer-events:none so clicks
    // fall through to the Mapbox canvas; handle active-pane tracking here.
    const leftClick  = () => setActivePane("left");
    const rightClick = () => setActivePane("right");
    left.on("click",  leftClick);
    right.on("click", rightClick);

    return () => {
      left.off('move',  syncRight);
      right.off('move', syncLeft);
      left.off("click",  leftClick);
      right.off("click", rightClick);
    };
  }, [mapLoaded, rightMapLoaded]);

  // ── Right renderer lifecycle (dual pane only) ───────────────────────────
  // Creates a RadarMapRenderer on the right map.  No playback interval —
  // the left pane's interval drives both via rightShowFrameRef.
  useEffect(() => {
    if (!rightMapLoaded || !isDualPane) {
      rightShowFrameRef.current = null;
      rightRendererRef.current?.destroy();
      rightRendererRef.current = null;
      rightFramesRef.current = [];
      return;
    }
    const rightMap = rightMapRef.current;
    if (!rightMap) return;

    rightFramesRef.current = [];
    rightRendererRef.current?.destroy();

    const renderer = new RadarMapRenderer(rightMap);
    rightRendererRef.current = renderer;

    // rightShowFrame: takes an explicit index — reads rightFramesRef,
    // renders on the right renderer.  Does NOT advance radarFrameIdxRef
    // or set React state (those are driven by the left pane).
    const rightShowFrame = (idx, { immediate = false } = {}) => {
      const fs = rightFramesRef.current;
      if (!fs.length) return;
      const i = idx < fs.length ? idx : idx % fs.length;
      const f = fs[i];
      if (!f) return;
      renderer.setFrame({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }, { immediate });
    };

    rightShowFrameRef.current = rightShowFrame;

    return () => {
      rightShowFrameRef.current = null;
      rightRendererRef.current?.destroy();
      rightRendererRef.current = null;
      rightFramesRef.current = [];
    };
  }, [rightMapLoaded, activeSite, isDualPane]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Right reflectivity manifest sync (dual pane only) ───────────────────
  useEffect(() => {
    if (!rightMapLoaded || !isDualPane) return;
    if (rightProduct === "velocity" || rightProduct === "mrms") return;

    const frames = manifest?.frames;
    if (!frames?.length) {
      rightFramesRef.current = [];
      return;
    }

    rightFramesRef.current = frames.map(f => ({
      ...f,
      tile_url_template: toAbsoluteUrl(normalizeUrl(f.tile_url_template)),
    }));

    rightRendererRef.current?.setFrames(
      rightFramesRef.current.map(f => ({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }))
    );

    rightShowFrameRef.current?.(radarFrameIdxRef.current, { immediate: true });
  }, [rightMapLoaded, isDualPane, manifest, rightProduct]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Right velocity manifest sync (dual pane only) ───────────────────────
  useEffect(() => {
    if (!rightMapLoaded || !isDualPane) return;
    if (rightProduct !== "velocity") return;

    const frames = rightVel.manifest?.frames;
    if (!frames?.length) {
      rightFramesRef.current = [];
      return;
    }

    rightFramesRef.current = frames.map(f => ({
      ...f,
      tile_url_template: toAbsoluteUrl(normalizeUrl(f.tile_url_template)),
    }));

    rightRendererRef.current?.setFrames(
      rightFramesRef.current.map(f => ({
        scanId:          f.scan_id,
        tileUrlTemplate: f.tile_url_template,
        tileZoomRange:   f.tile_zoom_range,
      }))
    );

    rightShowFrameRef.current?.(radarFrameIdxRef.current, { immediate: true });
  }, [rightMapLoaded, isDualPane, rightVel.manifest, rightProduct]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Opacity application ─────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const slot = activeLayerRef.current;
    if (usingTilesRef.current) {
      try { map.setPaintProperty(`radar-tile-layer-${slot}`, "raster-opacity", radarOpacity); } catch {}
    } else {
      map.setPaintProperty(`radar-layer-${slot}`, "raster-opacity", radarOpacity);
    }
  }, [mapLoaded, radarOpacity]);

  // Keep radarOpacityRef in sync so the crossfade effect always reads the
  // current value without taking a dependency on radarOpacity itself.
  useEffect(() => {
    radarOpacityRef.current = radarOpacity;
  }, [radarOpacity]);

  // ── Clear stale frame when frames drop to zero ───────────────────────────
  // Fires when a product/site switch empties the frame list while a new
  // manifest is loading.  Zeros both layers AND resets both image buffers to
  // the transparent placeholder — opacity-only is not enough because a stale
  // image can repaint if opacity is later restored before a new frame loads.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map || frameCount > 0) return;
    map.setPaintProperty("radar-layer-a", "raster-opacity", 0);
    map.setPaintProperty("radar-layer-b", "raster-opacity", 0);
    safeUpdateImage(map.getSource("radar-frame-a"), { url: TRANSPARENT_1PX, coordinates: PLACEHOLDER_COORDS });
    safeUpdateImage(map.getSource("radar-frame-b"), { url: TRANSPARENT_1PX, coordinates: PLACEHOLDER_COORDS });
    try { map.setPaintProperty("radar-tile-layer-a", "raster-opacity", 0); } catch {}
    try { map.setPaintProperty("radar-tile-layer-b", "raster-opacity", 0); } catch {}
  }, [mapLoaded, frameCount]);

  // ── Hard reset on product/site switch ────────────────────────────────────
  // Authoritative session boundary.  Effects run in declaration order so
  // this always fires BEFORE the crossfade on the same render, guaranteeing
  // that all downstream effects see the new session token immediately.
  //
  //  1. sessionRef++ — invalidates captured session values in the auto-play
  //     interval callback (self-terminates) and preload onload handlers
  //     (silently discarded) from the prior product/site session.
  //  2. clearInterval — kills the playback timer immediately, eliminating
  //     the window where a stale tick could call setFrameIndex before
  //     the auto-play effect restarts with the new session's frameCount.
  //  3. isSwitchingRef = true — signals the first crossfade of the new
  //     session to write directly onto layer-a (clean slate) and skip the
  //     double-buffer swap, preventing any ghost from the inactive buffer.
  //  4. Zero both layers + reset both image buffers — removes every visible
  //     remnant of the outgoing product/site before new frames arrive.
  //
  // radarDatasetKey encodes the real active dataset ("mrms" or "nexrad:<site>:<product>").
  // selectedRadarId is a UI-only range-ring selection and is NOT included here.
  useEffect(() => {
    sessionRef.current    += 1;
    isSwitchingRef.current = true;
    setDataEpoch((e) => e + 1);

    // Signal frames-sync to call loadFrames (not updateFrames) on next arrival.
    // engine.loadFrames([]) clears engine state immediately (calls pause internally).
    isFirstLoadRef.current = true;
    engineRef.current?.loadFrames([]);

    // Clear NEXRAD RadarMapRenderer frame state so the playback bar shows 0
    // frames immediately on product/site switch (before the new manifest arrives).
    radarFramesRef.current   = [];
    radarFrameIdxRef.current = 0;
    setRadarFrameCount(0);
    setRadarTimestamp(null);
    // Show "Loading..." only when a site is actually selected; stay idle (null)
    // when no site is active so the empty-state banner shows instead.
    setRadarLoadState(activeSite ? "loading" : null);

    // Clear failed-frame tracking — new session, new manifest, fresh URLs.
    failedFramesRef.current.clear();
    consecutiveFailsRef.current = 0;
    setFailedVersion(0);

    // Cancel any pending tile-readiness promotion from the prior session.
    pendingPromotionRef.current  = null;
    lastAppliedScanIdRef.current = null;
    if (promotionTimerRef.current) {
      clearTimeout(promotionTimerRef.current);
      promotionTimerRef.current = null;
    }
    tileReadiness.reset();

    const map = mapRef.current;
    if (!mapLoaded || !map) return;

    // System 2 layers (MRMS image+tile sources) — only clear when they may
    // have been active.  Skips 6 setPaintProperty + 2 updateImage calls when
    // switching between NEXRAD products/sites (the common reflectivity path).
    if (usingTilesRef.current === false || selectedProductRef.current === "mrms") {
      map.setPaintProperty("radar-layer-a", "raster-opacity", 0);
      map.setPaintProperty("radar-layer-b", "raster-opacity", 0);
      safeUpdateImage(map.getSource("radar-frame-a"), { url: TRANSPARENT_1PX, coordinates: PLACEHOLDER_COORDS });
      safeUpdateImage(map.getSource("radar-frame-b"), { url: TRANSPARENT_1PX, coordinates: PLACEHOLDER_COORDS });
      try { map.setPaintProperty("radar-tile-layer-a", "raster-opacity", 0); } catch {}
      try { map.setPaintProperty("radar-tile-layer-b", "raster-opacity", 0); } catch {}
    }
    // Hide RadarMapRenderer tile layers so the outgoing product's tiles are not
    // visible while the new manifest is loading.  URL-mismatch detection in
    // setFrame/setFrames rebuilds slots automatically when the new product's tile
    // URLs differ (reflectivity and velocity share NEXRAD Level-II scanIds but
    // use different tile servers, so the URL check catches the collision).
    p4RendererRef.current?.hideAll();
    activeLayerRef.current = "a";
    usingTilesRef.current  = false;
  }, [radarDatasetKey, mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Frames sync → engine ─────────────────────────────────────────────────
  // Declared AFTER the hard reset effect so React runs hard reset first on any
  // render where both deps change (e.g. dataset switch with stale frames).
  // isFirstLoadRef (set by hard reset) decides loadFrames vs updateFrames so
  // stale frames from the prior session never reach engine.loadFrames.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    // System 1 (RadarMapRenderer) handles all NEXRAD products.
    // Keep the engine idle outside MRMS so it fires no frameChange events
    // and drives no tile fetches via the crossfade path.
    if (selectedProduct !== "mrms") {
      engine.loadFrames([]);
      return;
    }

    if (playbackFrames.length === 0) {
      engine.loadFrames([]);
      return;
    }

    if (isFirstLoadRef.current) {
      engine.loadFrames(playbackFrames);
      isFirstLoadRef.current = false;
      // loadFrames() resets index to 0 (oldest). Jump to newest immediately so
      // the first visible frame is current conditions, not stale history.
      engine.jumpToLatest();
      // onMrmsActivate called engine.play() before frames existed (play()
      // short-circuits on empty arrays). Re-issue play now that frames are loaded.
      if (liveModeRef.current) engine.play();
    } else {
      engine.updateFrames(playbackFrames);
    }
  }, [playbackFrames, selectedProduct]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Radar layer crossfade ───────────────────────────────────────────────
  // Clamp inline: the frameIndex-clamping effect lags one render cycle when
  // frames shrinks (e.g. MRMS rolling window shifts).  Clamping here closes
  // that window so we never dereference an out-of-range slot.
  const currentFrame = (frameCount > 0 && frameIndex < frameCount) ? playbackFrames[frameIndex] : null;

  // Keep a ref so the stable dBZ probe closure can always read the exact URL
  // for the frame that is currently painted — avoids reading from stale/off-screen
  // dual-buffer NEXRAD sources.
  useEffect(() => {
    probeTileUrlRef.current = currentFrame?.tileUrlTemplate
      ? toAbsoluteUrl(normalizeUrl(currentFrame.tileUrlTemplate))
      : null;
  }, [currentFrame]);

  useEffect(() => {
    // System 1 (RadarMapRenderer) handles all NEXRAD products.
    // Bail early to prevent duplicate radar-tile-layer-a/b tile fetches.
    // Use both the ref (fast path) and the closure-captured state value
    // (belt-and-suspenders against stale ref during rapid product switches).
    if (selectedProductRef.current !== "mrms") return;
    if (selectedProduct !== "mrms") return;

    // Capture session at effect entry.  If a concurrent hard reset has already
    // bumped the session (e.g. a stale setFrameIndex update batched in the
    // same render), bail out — the reset has already cleared the layers.
    const session = sessionRef.current;
    const map     = mapRef.current;
    if (!mapLoaded || !map || !currentFrame) return;

    // Tile mode when tileUrlTemplate is present; image-source as fallback.
    const useTiles = !!currentFrame.tileUrlTemplate;

    // Guard: need a valid frame identity + bounds.
    // Bounds are only required for image mode (used to position the overlay).
    // Tile mode is self-positioning via XYZ coordinates — skip the bounds check.
    if (!useTiles && !hasValidBounds(currentFrame.bounds)) {
      return;
    }
    if (useTiles && !currentFrame.scanId) {
      return;
    }
    if (!useTiles && !currentFrame.url) {
      return;
    }
    if (session !== sessionRef.current) {
      return;
    }

    // De-duplicate: skip if this exact scan is already applied or pending.
    const frameKey = currentFrame.scanId || currentFrame.url;
    if (frameKey && frameKey === lastAppliedScanIdRef.current && !isSwitchingRef.current) {
      return;
    }

    // Don't stomp a pending promotion — the prior frame is still loading.
    // The auto-play timer will retry after the pending promotion completes.
    if (pendingPromotionRef.current && !isSwitchingRef.current) {
      return;
    }

    lastAppliedScanIdRef.current = frameKey;
    consecutiveFailsRef.current = 0;  // reset on successful frame application

    const tileLayerName = (slot) => `radar-tile-layer-${slot}`;
    const imgLayerName  = (slot) => `radar-layer-${slot}`;

    // Helper: apply a frame to a slot (tile or image mode).
    const applyFrame = (slot) => {
      if (useTiles) {
        // Use the server-provided tile URL template — works for both NEXRAD and
        // MRMS.  The old hardcoded pattern used scanId.slice(0,4) as a NEXRAD
        // station code, which is wrong for MRMS scan IDs (e.g. "20240101_...").
        const tileUrl = toAbsoluteUrl(normalizeUrl(currentFrame.tileUrlTemplate));
        setTileSourceUrl(map, slot, tileUrl, currentFrame.tileZoomRange);
      } else {
        const src = map.getSource(`radar-frame-${slot}`);
        if (!src || typeof src.updateImage !== "function") return false;
        safeUpdateImage(src, { url: currentFrame.url, coordinates: boundsToCoords(currentFrame.bounds) });
      }
      return true;
    };

    if (isSwitchingRef.current) {
      // First frame after a product/site switch: write directly onto slot-a.
      if (session !== sessionRef.current) return;

      // NOTE: a stale-manifest guard (comparing scanId prefix to activeSite)
      // was removed from here.  That guard was designed for NEXRAD site-switching
      // but this effect is MRMS-only (enforced by the early returns above).
      // MRMS scan IDs do not encode a NEXRAD site prefix, so the check always
      // fired when activeSite was set — permanently blocking MRMS display by
      // keeping isSwitchingRef.current=true and preventing applyFrame() from
      // ever running.

      applyFrame("a");
      usingTilesRef.current = useTiles;

      const showLayer = useTiles ? tileLayerName("a") : imgLayerName("a");
      const hideLayer = useTiles ? imgLayerName("a")  : tileLayerName("a");

      // Delay opacity reveal until the next animation frame so tile source
      // remove/re-add (in setTileSourceUrl) has settled — prevents a single-
      // frame flicker where neither slot has content.
      requestAnimationFrame(() => {
        if (session !== sessionRef.current) return; // session may have changed
        map.setPaintProperty(showLayer, "raster-opacity", radarOpacityRef.current);
        try { map.setPaintProperty(hideLayer, "raster-opacity", 0); } catch {}
        // Also hide b-slot layers
        map.setPaintProperty(imgLayerName("b"), "raster-opacity", 0);
        try { map.setPaintProperty(tileLayerName("b"), "raster-opacity", 0); } catch {}
      });

      activeLayerRef.current = "a";
      isSwitchingRef.current = false;
      return;
    }

    // Normal double-buffer crossfade for frame-to-frame playback within a session.
    const front = activeLayerRef.current;
    const back  = front === "a" ? "b" : "a";
    if (session !== sessionRef.current) return;
    applyFrame(back);
    usingTilesRef.current = useTiles;

    // Build the promotion closure — swaps opacity to reveal back, hide front.
    // Once-guard: promote can only fire once per pending frame.
    let promoted = false;
    const promote = () => {
      if (promoted) return;                        // already fired for this frame
      if (session !== sessionRef.current) return;  // stale — hard reset fired
      promoted = true;
      const showLayer = useTiles ? tileLayerName(back)  : imgLayerName(back);
      const hideShow  = useTiles ? imgLayerName(back)   : tileLayerName(back);
      const hideFront = useTiles ? tileLayerName(front)  : imgLayerName(front);
      const hideFrontAlt = useTiles ? imgLayerName(front) : tileLayerName(front);

      map.setPaintProperty(showLayer, "raster-opacity", radarOpacityRef.current);
      try { map.setPaintProperty(hideShow, "raster-opacity", 0); } catch {}
      try { map.setPaintProperty(hideFront, "raster-opacity", 0); } catch {}
      try { map.setPaintProperty(hideFrontAlt, "raster-opacity", 0); } catch {}

      activeLayerRef.current = back;
      pendingPromotionRef.current = null;
      if (promotionTimerRef.current) {
        clearTimeout(promotionTimerRef.current);
        promotionTimerRef.current = null;
      }
    };

    if (!useTiles) {
      // Image mode: promote immediately (images are preloaded by the existing
      // frame preloader and don't need readiness gating).
      promote();
    } else {
      // Tile mode: load tiles into back buffer, gate promotion on readiness.
      // Old frame stays visible until tiles are loaded or timeout fires.
      const tileSourceId = `radar-tiles-${back}`;
      tileReadiness.arm(tileSourceId);
      pendingPromotionRef.current = promote;

      // Timeout fallback — never stall playback longer than TILE_READY_TIMEOUT.
      if (promotionTimerRef.current) clearTimeout(promotionTimerRef.current);
      promotionTimerRef.current = setTimeout(() => {
        if (pendingPromotionRef.current) {
          pendingPromotionRef.current();
        }
      }, TILE_READY_TIMEOUT);
    }
  }, [mapLoaded, currentFrame]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tile readiness watcher — fires pending promotion when tiles are loaded ─
  useEffect(() => {
    if (!tileReadiness.ready || !pendingPromotionRef.current) return;
    pendingPromotionRef.current();
  }, [tileReadiness.ready]);

  // ── Map click — measure points + clear selections ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    function onMapClick(e) {
      const tool = activeMapToolRef.current;
      if (tool === "measure") {
        const pt = [e.lngLat.lng, e.lngLat.lat];
        if (measureFinalizedRef.current) {
          // 3rd click — start a new measurement
          setMeasurePoints([pt]);
          setMeasureFinalized(false);
        } else {
          setMeasurePoints((prev) => {
            if (prev.length === 0) return [pt];          // 1st click — set center
            // 2nd click — finalize
            setMeasureFinalized(true);
            return [prev[0], pt];
          });
        }
        return;
      }
      if (tool === "dbz") return; // probe mode — clicks don't place anything
      // Clear both selections when clicking empty map (no site dot, no warning polygon).
      const siteHits = map.queryRenderedFeatures(e.point, { layers: ["radar-sites-layer"] });
      const warnHits = map.getLayer("warnings-fill")
        ? map.queryRenderedFeatures(e.point, { layers: ["warnings-fill"] })
        : [];
      if (siteHits.length === 0 && warnHits.length === 0) {
        setSelectedRadarId(null);
        setSelectedWarningId(null);
        setSelectedWarning(null);
        setSelectedWarnings([]);
        setWarnSheetOpen(false);
        setSelectedMD(null);
        setMdPopupPos(null);
        setSelectedMetar(null);
        setMetarPopupPos(null);
      }
    }
    map.on("click", onMapClick);
    return () => map.off("click", onMapClick);
  }, [mapLoaded]);

  // ── Radial ruler — live radius preview on desktop mousemove ────────────
  // Touch devices: preview is deferred (touchmove conflicts with map pan).
  // The user taps center, then taps edge to finalize.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    function onMouseMove(e) {
      if (activeMapToolRef.current !== "measure") return;
      if (measureFinalizedRef.current) return;
      setMeasurePoints((prev) => {
        if (prev.length < 1) return prev;             // no center yet
        return [prev[0], [e.lngLat.lng, e.lngLat.lat]]; // live edge
      });
    }
    map.on("mousemove", onMouseMove);
    return () => map.off("mousemove", onMouseMove);
  }, [mapLoaded]);

  // ── Long-press to switch to nearest supported radar site ───────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const HOLD_MS = 1200;
    const DRAG_PX = 8; // movement threshold to cancel
    let timer = null;
    let startPt = null;
    function startHold(e) {
      // Don't trigger during active tools or multi-touch (pinch)
      if (activeMapToolRef.current !== null) return;
      if (e.originalEvent?.touches?.length > 1) { endHold(); return; }
      holdFiredRef.current = false;
      startPt = e.point ?? { x: e.originalEvent?.clientX, y: e.originalEvent?.clientY };
      timer = setTimeout(() => {
        const lngLat = e.lngLat;
        if (!lngLat) return;
        // Find nearest site with hasData: true
        const supported = RADAR_SITES_CATALOG.filter((s) => s.hasData);
        if (supported.length === 0) return;
        let best = null;
        let bestDist = Infinity;
        for (const site of supported) {
          const dlat = site.lat - lngLat.lat;
          const dlng = (site.lon - lngLat.lng) * Math.cos((lngLat.lat * Math.PI) / 180);
          const d = dlat * dlat + dlng * dlng;
          if (d < bestDist) { bestDist = d; best = site; }
        }
        if (!best) return;
        // Skip if already on this site
        if (selectedRadarIdRef.current === best.id) return;
        holdFiredRef.current = true; // mark so the trailing click event is ignored
        setSelectedRadarId(best.id);
        if (selectedProductRef.current === "mrms") {
          setSelectedProduct("reflectivity");
        }
        onRadarSiteSelect?.(best.id);
        setSiteSwitchToast(`Switched to ${best.id}`);
        timer = null;
      }, HOLD_MS);
    }

    function cancelHold(e) {
      if (!timer) return;
      if (e && startPt) {
        const pt = e.point ?? { x: e.originalEvent?.clientX, y: e.originalEvent?.clientY };
        if (pt) {
          const dx = pt.x - startPt.x;
          const dy = pt.y - startPt.y;
          if (dx * dx + dy * dy > DRAG_PX * DRAG_PX) {
            clearTimeout(timer);
            timer = null;
            return;
          }
        }
      }
    }

    function endHold() {
      if (timer) { clearTimeout(timer); timer = null; }
    }

    map.on("mousedown",  startHold);
    map.on("mousemove",  cancelHold);
    map.on("mouseup",    endHold);
    map.on("touchstart", startHold);
    map.on("touchmove",  cancelHold);
    map.on("touchend",   endHold);

    // Mirror on right map — startHold/cancelHold/endHold only reference
    // shared refs and lngLat from the event, not the map object itself.
    const rmap = rightMapRef.current;
    if (rmap && rightMapLoaded) {
      rmap.on("mousedown",  startHold);
      rmap.on("mousemove",  cancelHold);
      rmap.on("mouseup",    endHold);
      rmap.on("touchstart", startHold);
      rmap.on("touchmove",  cancelHold);
      rmap.on("touchend",   endHold);
    }

    return () => {
      if (timer) clearTimeout(timer);
      map.off("mousedown",  startHold);
      map.off("mousemove",  cancelHold);
      map.off("mouseup",    endHold);
      map.off("touchstart", startHold);
      map.off("touchmove",  cancelHold);
      map.off("touchend",   endHold);
      if (rmap && rightMapLoaded) {
        rmap.off("mousedown",  startHold);
        rmap.off("mousemove",  cancelHold);
        rmap.off("mouseup",    endHold);
        rmap.off("touchstart", startHold);
        rmap.off("touchmove",  cancelHold);
        rmap.off("touchend",   endHold);
      }
    };
  }, [mapLoaded, rightMapLoaded, onRadarSiteSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear site switch toast
  useEffect(() => {
    if (!siteSwitchToast) return;
    const id = setTimeout(() => setSiteSwitchToast(null), 2000);
    return () => clearTimeout(id);
  }, [siteSwitchToast]);

  // ── Disable map drag while annotating ────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!map) return;

    if (isDrawMode) {
      map.dragPan.disable();
      right?.dragPan.disable();
    } else {
      map.dragPan.enable();
      right?.dragPan.enable();
    }
  }, [isDrawMode, rightMapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync measure sources + cursor ────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    map.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : "";
    map.getSource("measure-pts")?.setData({
      type: "FeatureCollection",
      features: measurePoints.map((p) => ({
        type: "Feature", geometry: { type: "Point", coordinates: p }, properties: {},
      })),
    });
    // Radial ruler: ring + radial line as two features in one FeatureCollection
    if (measurePoints.length === 2) {
      const center   = measurePoints[0];
      const edge     = measurePoints[1];
      const radiusKm = haversineMeters(center, edge) / 1000;
      const ringGeo  = radiusKm > 0.001
        ? generateRangeRingGeoJson(center[1], center[0], radiusKm)
        : { type: "FeatureCollection", features: [] };
      // Combine ring + radial line into one FeatureCollection
      map.getSource("measure-ln")?.setData({
        type: "FeatureCollection",
        features: [
          ...ringGeo.features,
          {
            type: "Feature",
            geometry: { type: "LineString", coordinates: [center, edge] },
            properties: {},
          },
        ],
      });
    } else {
      map.getSource("measure-ln")?.setData({
        type: "FeatureCollection", features: [],
      });
    }
  }, [mapLoaded, measurePoints, activeMapTool]);

  // ── Measure label screen position (midpoint of radial line) ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map || measurePoints.length < 2) { setMeasureLabelPx(null); return; }
    function update() {
      const mid = [
        (measurePoints[0][0] + measurePoints[1][0]) / 2,
        (measurePoints[0][1] + measurePoints[1][1]) / 2,
      ];
      const px = map.project(mid);
      setMeasureLabelPx({ x: px.x, y: px.y });
    }
    update();
    map.on("move", update);
    map.on("zoom", update);
    return () => { map.off("move", update); map.off("zoom", update); };
  }, [mapLoaded, measurePoints]);

  // ── Radar site health → map source sync ──────────────────────────────────
  // Rebuilds the GeoJSON with updated health properties whenever the health
  // map changes, so Mapbox re-renders dot colors without touching the layers.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const src = map.getSource("radar-sites-source");
    if (!src || typeof src.setData !== "function") return;
    src.setData(buildRadarSitesGeoJson(siteHealthMap));
  }, [mapLoaded, siteHealthMap]);

  // ── Radar sites visibility sync ──────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    setRadarSitesVisibility(map, radarSitesVisible);
  }, [mapLoaded, radarSitesVisible]);

  // ── Fronts data fetch ───────────────────────────────────────────────────
  // NOAA WPC National Forecast Chart:
  //   Layer 1 = Pressure Centers (Point, feat: "High Valid" / "Low Valid")
  //   Layer 2 = Fronts (MultiLineString)
  // Both merged into a single FeatureCollection feeding fronts-source.
  useEffect(() => {
    if (!showFronts) return;
    const ctrl = new AbortController();
    const BASE =
      "https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/natl_fcst_wx_chart/MapServer";
    const QS = "/query?where=1%3D1&outFields=*&f=geojson";
    const fetchFronts = async () => {
      try {
        const [frontsRes, pressureRes] = await Promise.all([
          fetch(`${BASE}/2${QS}`, { signal: ctrl.signal, cache: "no-store" }),
          fetch(`${BASE}/1${QS}`, { signal: ctrl.signal, cache: "no-store" }),
        ]);
        const frontsGj = frontsRes.ok ? await frontsRes.json() : null;
        const pressureGj = pressureRes.ok ? await pressureRes.json() : null;
        const features = [
          ...(frontsGj?.features || []),
          ...(pressureGj?.features || []),
        ];
        setFrontsError(false);
        if (features.length) {
          setFrontsData({ type: "FeatureCollection", features });
        }
      } catch { setFrontsError(true); }
    };
    fetchFronts();
    const id = setInterval(fetchFronts, 15 * 60 * 1000);
    return () => { ctrl.abort(); clearInterval(id); };
  }, [showFronts]);

  // ── Fronts visibility sync ─────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (mapLoaded && map) setFrontsVisibility(map, showFronts);
    const right = rightMapRef.current;
    if (rightMapLoaded && right) setFrontsVisibility(right, showFronts);
    localStorage.setItem("sa_showFronts", showFronts ? "1" : "0");
  }, [mapLoaded, rightMapLoaded, showFronts]);

  // ── Fronts data sync ───────────────────────────────────────────────────
  useEffect(() => {
    if (!frontsData) return;
    const map = mapRef.current;
    if (mapLoaded && map) map.getSource("fronts-source")?.setData(frontsData);
    const right = rightMapRef.current;
    if (rightMapLoaded && right) right.getSource("fronts-source")?.setData(frontsData);
  }, [mapLoaded, rightMapLoaded, frontsData]);

  // ── MD data fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mdEnabled) return;
    const ctrl = new AbortController();
    const fetchMds = async () => {
      try {
        const res = await fetch(`${API_BASE}/mds/geojson`, { signal: ctrl.signal, cache: "no-store" });
        if (!res.ok) return;
        const gj = await res.json();
        if (gj?.features) setMdData(gj);
      } catch (err) {
        if (err?.name !== "AbortError") console.warn("[md_fetch] failed:", err);
      }
    };
    fetchMds();
    const id = setInterval(fetchMds, 10 * 60 * 1000); // 10 min — matches backend poll
    return () => { ctrl.abort(); clearInterval(id); };
  }, [mdEnabled]);

  // ── MD visibility + type-filter sync ────────────────────────────────────
  useEffect(() => {
    const applyToMap = (map) => {
      if (!map) return;
      setMdVisibility(map, mdEnabled);
      if (mdEnabled) {
        const active = Object.entries(mdFilters).filter(([, v]) => v).map(([k]) => k);
        const filterExpr = active.length === 0
          ? ["==", ["literal", false], true]                       // nothing active → hide all
          : active.length === Object.keys(mdFilters).length
          ? null                                                   // all active → no filter
          : ["in", ["get", "type"], ["literal", active]];
        ["md-polygons", "md-outline"].forEach((id) => {
          if (map.getLayer(id)) map.setFilter(id, filterExpr);
        });
      }
    };
    if (mapLoaded)      applyToMap(mapRef.current);
    if (rightMapLoaded) applyToMap(rightMapRef.current);
  }, [mapLoaded, rightMapLoaded, mdEnabled, mdFilters]);

  // ── MD data source sync ──────────────────────────────────────────────────
  useEffect(() => {
    if (!mdData) return;
    const map = mapRef.current;
    if (mapLoaded && map) map.getSource("md-source")?.setData(mdData);
    const rmap = rightMapRef.current;
    if (rightMapLoaded && rmap) rmap.getSource("md-source")?.setData(mdData);
  }, [mapLoaded, rightMapLoaded, mdData]);

  // ── MD label markers ─────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    // Always remove stale markers first
    mdMarkersRef.current.forEach(m => m.remove());
    mdMarkersRef.current = [];
    if (!mapLoaded || !map || !mdEnabled || !mdData?.features?.length) return;

    const zoom = map.getZoom();
    mdData.features.forEach((feat) => {
      const props = feat.properties || {};
      const type  = props.type || "severe";
      if (!mdFilters[type]) return; // respect active type filter
      const center = mdLabelCenter(feat.geometry);
      if (!center) return;

      const el = document.createElement("div");
      el.className = `md-label md-label--${type}`;
      el.innerHTML =
        `<div class="md-label__id">MD #${String(props.id ?? "").padStart(4, "0")}</div>` +
        `<div class="md-label__tag">${mdLabelTag({ type, watch_probability: props.watch_probability, watch_tag: props.watch_tag })}</div>`;
      if (zoom < 5) el.style.display = "none";

      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat(center)
        .addTo(map);
      mdMarkersRef.current.push(marker);
    });

    const onZoom = () => {
      const z = map.getZoom();
      mdMarkersRef.current.forEach(m => {
        m.getElement().style.display = z >= 5 ? "" : "none";
      });
    };
    map.on("zoom", onZoom);

    return () => {
      map.off("zoom", onZoom);
      mdMarkersRef.current.forEach(m => m.remove());
      mdMarkersRef.current = [];
    };
  }, [mapLoaded, mdData, mdEnabled, mdFilters]);

  // ── MD polygon click handler ─────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const onMdClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      const feat = e.features?.[0];
      if (!feat) return;
      const ring = feat.geometry?.type === "Polygon"
        ? feat.geometry.coordinates[0]
        : feat.geometry?.type === "MultiPolygon"
        ? feat.geometry.coordinates[0][0]
        : null;
      let center = null;
      if (ring?.length) {
        let lo = 0, la = 0;
        ring.forEach(([x, y]) => { lo += x; la += y; });
        center = [lo / ring.length, la / ring.length];
      }
      setSelectedMD({ ...feat.properties, _center: center });
      const c = map.getContainer();
      setMdPopupPos(clampPopupPos(e.point.x, e.point.y, c.clientWidth, c.clientHeight));
    };
    map.on("click", "md-polygons", onMdClick);
    return () => map.off("click", "md-polygons", onMdClick);
  }, [mapLoaded]);

  // ── METAR fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!metarEnabled) return;
    const ctrl = new AbortController();
    const fetchMetars = async () => {
      try {
        const res = await fetch(`${API_BASE}/metars/geojson`, { signal: ctrl.signal, cache: "no-store" });
        if (!res.ok) {
          console.warn(`[METAR] fetch failed: HTTP ${res.status}`);
          return;
        }
        const gj = await res.json();
        if (gj?.features) setMetarData(enrichMetarData(gj));
      } catch (err) {
        if (err.name !== "AbortError") console.warn("[METAR] fetch error:", err);
      }
    };
    fetchMetars();
    const id = setInterval(fetchMetars, 5 * 60 * 1000);
    return () => { ctrl.abort(); clearInterval(id); };
  }, [metarEnabled]);

  // ── METAR visibility ──────────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map) return;
    setMetarVisibility(map, metarEnabled);
    if (rightMapLoaded && right) setMetarVisibility(right, metarEnabled);
    if (!metarEnabled) { setSelectedMetar(null); setMetarPopupPos(null); }
  }, [mapLoaded, rightMapLoaded, metarEnabled]);

  // ── METAR wind barb frame-sync rotation ───────────────────────────────────
  useEffect(() => {
    if (!metarEnabled || !mapLoaded) return;
    const maps = [mapRef.current, rightMapLoaded ? rightMapRef.current : null]
      .filter(Boolean);
    const deg  = Math.round((frameIndex * 360) / Math.max(frameCount, 1)) % 360;
    const expr = ["+", ["coalesce", ["get", "wdirNum"], 0], deg];
    for (const map of maps) {
      if (map?.getLayer("metar-wind-layer")) {
        map.setLayoutProperty("metar-wind-layer", "icon-rotate", expr);
      }
    }
    return () => {
      for (const map of maps) {
        if (map?.getLayer("metar-wind-layer")) {
          map.setLayoutProperty(
            "metar-wind-layer",
            "icon-rotate",
            ["coalesce", ["get", "wdirNum"], 0],
          );
        }
      }
    };
  }, [metarEnabled, mapLoaded, rightMapLoaded, frameIndex, frameCount]);

  // ── METAR source data sync ────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map || !metarData) return;
    const src = map.getSource("metar-source");
    if (src) src.setData(metarData);
    if (rightMapLoaded && right) {
      right.getSource("metar-source")?.setData(metarData);
    }
  }, [mapLoaded, rightMapLoaded, metarData]);

  // ── METAR click + cursor ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const onClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      const feat = e.features?.[0];
      if (!feat) return;
      const c = map.getContainer();
      setSelectedMetar(feat.properties);
      setMetarPopupPos(clampPopupPos(e.point.x, e.point.y, c.clientWidth, c.clientHeight));
    };
    const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onLeave = () => { map.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : ""; };
    map.on("click",      "metar-sky-layer", onClick);
    map.on("mouseenter", "metar-sky-layer", onEnter);
    map.on("mouseleave", "metar-sky-layer", onLeave);
    return () => {
      map.off("click",      "metar-sky-layer", onClick);
      map.off("mouseenter", "metar-sky-layer", onEnter);
      map.off("mouseleave", "metar-sky-layer", onLeave);
    };
  }, [mapLoaded, activeMapTool]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Spotter visibility ────────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map) return;
    setSpotterVisibility(map, spotterEnabled);
    if (rightMapLoaded && right) setSpotterVisibility(right, spotterEnabled);
    if (!spotterEnabled) { setSelectedSpotter(null); setSpotterPopupPos(null); }
  }, [mapLoaded, rightMapLoaded, spotterEnabled]);

  // ── Spotter data sync ─────────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map) return;
    const gj = spottersToGeoJson(MOCK_SPOTTERS);
    map.getSource("spotter-source")?.setData(gj);
    if (rightMapLoaded && right) right.getSource("spotter-source")?.setData(gj);
  }, [mapLoaded, rightMapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Report visibility ─────────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map) return;
    setReportVisibility(map, reportsEnabled);
    if (rightMapLoaded && right) setReportVisibility(right, reportsEnabled);
    if (!reportsEnabled) { setSelectedReport(null); }
  }, [mapLoaded, rightMapLoaded, reportsEnabled]);

  // ── Report filter (source + type chips) ───────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map || !reportsEnabled) return;
    setReportFilter(map, reportSources, reportTypes);
    if (rightMapLoaded && right) setReportFilter(right, reportSources, reportTypes);
  }, [mapLoaded, rightMapLoaded, reportsEnabled, reportSources, reportTypes]);

  // ── mPING fetch — refresh every 60 s ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      const reports = await fetchMPingReports();
      if (!cancelled) setMpingReports(reports);
    }
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Report data sync ──────────────────────────────────────────────────────
  useEffect(() => {
    const map   = mapRef.current;
    const right = rightMapRef.current;
    if (!mapLoaded || !map) return;
    const staticReports = MOCK_REPORTS.filter((r) => r.source !== "mping");
    const gj = reportsToGeoJson([...staticReports, ...mpingReports]);
    map.getSource("report-source")?.setData(gj);
    if (rightMapLoaded && right) right.getSource("report-source")?.setData(gj);
  }, [mapLoaded, rightMapLoaded, mpingReports]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Spotter / Report click + cursor ──────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const c = map.getContainer();
    const onSpotterClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      const feat = e.features?.[0];
      if (!feat) return;
      setSelectedReport(null);
      setSelectedSpotter(feat.properties);
      setSpotterPopupPos(clampPopupPos(e.point.x, e.point.y, c.clientWidth, c.clientHeight));
    };
    const onReportClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      const feat = e.features?.[0];
      if (!feat) return;
      setSelectedSpotter(null);
      setSelectedReport(feat.properties);
      setSpotterPopupPos(clampPopupPos(e.point.x, e.point.y, c.clientWidth, c.clientHeight));
    };
    const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onLeave = () => { map.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : ""; };
    map.on("click",      "spotter-points", onSpotterClick);
    map.on("click",      "report-points",  onReportClick);
    map.on("mouseenter", "spotter-points", onEnter);
    map.on("mouseenter", "report-points",  onEnter);
    map.on("mouseleave", "spotter-points", onLeave);
    map.on("mouseleave", "report-points",  onLeave);
    return () => {
      map.off("click",      "spotter-points", onSpotterClick);
      map.off("click",      "report-points",  onReportClick);
      map.off("mouseenter", "spotter-points", onEnter);
      map.off("mouseenter", "report-points",  onEnter);
      map.off("mouseleave", "spotter-points", onLeave);
      map.off("mouseleave", "report-points",  onLeave);
    };
  }, [mapLoaded, activeMapTool]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Radar resampling — zoom-reactive (legacy layer set) ──────────────────
  // raster-resampling cannot be set via expression; setPaintProperty on zoom.
  // linear ≤ z8 (native tile resolution), nearest > z8 (overzoomed, crisp).
  // RadarMapRenderer handles its own layers internally via its zoom listener.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const handler = () => syncRadarResampling(map);
    handler(); // sync for current zoom on mount
    map.on("zoom", handler);
    return () => { map.off("zoom", handler); };
  }, [mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Radar site hover cursor + click-to-select ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onLeave = () => { map.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : ""; };
    const onSiteClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      // Suppress the click that immediately follows a hold gesture — the hold
      // already switched to the nearest site; the click would fight it.
      if (holdFiredRef.current) { holdFiredRef.current = false; return; }
      const id = e.features?.[0]?.properties?.id;
      if (!id) return;
      // Toggle: clicking the already-selected site deselects it.
      const nextId = selectedRadarIdRef.current === id ? null : id;
      setSelectedWarningId(null);
      setSelectedWarning(null);
      setSelectedWarnings([]);
      setWarnSheetOpen(false);
      setSelectedRadarId(nextId);
      // Switch to site radar.  If MRMS is active, transition to NEXRAD
      // Level II automatically — no need to change product manually first.
      if (selectedProductRef.current === "mrms" && nextId) {
        setSelectedProduct("reflectivity");
      }
      onRadarSiteSelect?.(nextId);
    };
    map.on("mouseenter", "radar-sites-layer", onEnter);
    map.on("mouseleave", "radar-sites-layer", onLeave);
    map.on("click",      "radar-sites-layer", onSiteClick);

    // Mirror on right map when dual-pane is active.  onSiteClick is shared
    // (it reads e.features and calls shared state — no map reference).
    // Cursor handlers need separate closures to target the right canvas.
    const rmap = rightMapRef.current;
    const onEnterRight = () => { try { rmap.getCanvas().style.cursor = "pointer"; } catch {} };
    const onLeaveRight = () => { try { rmap.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : ""; } catch {} };
    if (rmap && rightMapLoaded) {
      rmap.on("mouseenter", "radar-sites-layer", onEnterRight);
      rmap.on("mouseleave", "radar-sites-layer", onLeaveRight);
      rmap.on("click",      "radar-sites-layer", onSiteClick);
    }

    return () => {
      map.off("mouseenter", "radar-sites-layer", onEnter);
      map.off("mouseleave", "radar-sites-layer", onLeave);
      map.off("click",      "radar-sites-layer", onSiteClick);
      if (rmap && rightMapLoaded) {
        rmap.off("mouseenter", "radar-sites-layer", onEnterRight);
        rmap.off("mouseleave", "radar-sites-layer", onLeaveRight);
        rmap.off("click",      "radar-sites-layer", onSiteClick);
      }
    };
  }, [mapLoaded, rightMapLoaded, activeMapTool, onRadarSiteSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Range ring sync ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const src = map.getSource("range-ring-source");
    if (!src) return;
    if (!selectedRadarId) {
      src.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    const site = RADAR_SITES_CATALOG.find((s) => s.id === selectedRadarId);
    if (!site) { src.setData({ type: "FeatureCollection", features: [] }); return; }
    const rangeKm = site.rangeKm ?? RADAR_NETWORKS[site.networkId]?.rangeKm ?? 230;
    src.setData(generateRangeRingGeoJson(site.lat, site.lon, rangeKm));
  }, [mapLoaded, selectedRadarId]);

  // ── Warning polygons: sync GeoJSON source ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    // Filter by enabled warning types; unrecognised hazard_types always show.
    const filtered = activeWarnings.filter((w) => {
      const ht = w.hazard_type ?? "unknown";
      const controlled = WARNING_TYPES.find((t) => t.hazard_type === ht);
      return controlled ? warningSettings[controlled.key]?.enabled !== false : true;
    });
    map.getSource("warnings-source")?.setData(warningsToGeoJson(filtered));
  }, [mapLoaded, activeWarnings, warningSettings]);

  // ── Warning polygons: sync fill/outline colors ───────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const expr = buildWarnColorExpr(warningSettings);
    if (map.getLayer("warnings-fill"))    map.setPaintProperty("warnings-fill",    "fill-color", expr);
    if (map.getLayer("warnings-outline")) map.setPaintProperty("warnings-outline", "line-color",  expr);
  }, [mapLoaded, warningSettings]);

  // ── Warning polygons: sync highlight filter ───────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map || !map.getLayer("warnings-highlight")) return;
    map.setFilter(
      "warnings-highlight",
      selectedWarningId
        ? ["==", ["get", "id"], selectedWarningId]
        : ["==", ["get", "id"], ""],
    );
  }, [mapLoaded, selectedWarningId]);

  // ── Warning polygons: hover highlight + cursor + click-to-focus ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    let hoveredId = null;
    const onEnter = (e) => {
      map.getCanvas().style.cursor = "pointer";
      const id = e.features?.length
        ? e.features.reduce((best, f) =>
            (f.properties?.severity_rank ?? 13) < (best.properties?.severity_rank ?? 13) ? f : best
          ).id
        : undefined;
      if (id == null) return;
      if (hoveredId !== null) map.setFeatureState({ source: "warnings-source", id: hoveredId }, { hover: false });
      hoveredId = id;
      map.setFeatureState({ source: "warnings-source", id: hoveredId }, { hover: true });
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = activeMapTool !== null ? "crosshair" : "";
      if (hoveredId !== null) {
        map.setFeatureState({ source: "warnings-source", id: hoveredId }, { hover: false });
        hoveredId = null;
      }
    };
    const onWarnClick = (e) => {
      if (activeMapToolRef.current !== null) return;
      if (!e.features?.length) return;
      // Deduplicate by warning id, sort by severity_rank (ascending = most severe)
      const seen = new Set();
      const sortedFeats = [...e.features]
        .filter(f => {
          const id = f.properties?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .sort((a, b) => (a.properties?.severity_rank ?? 13) - (b.properties?.severity_rank ?? 13));
      const matched = sortedFeats
        .map(f => {
          const w = activeWarningsRef.current.find(w => w.id === f.properties.id);
          if (!w) return null;
          const fullText = [w.headline, w.description, w.instruction].filter(Boolean).join("\n\n");
          return { ...w, fullText, obsType: getWarningObsType(fullText) };
        })
        .filter(Boolean);
      if (!matched.length) return;
      // Desktop: single floating card (existing behavior)
      setSelectedWarning(matched[0]);
      setSelectedWarningId(matched[0].id ?? null);
      const container = mapContainerRef.current;
      const cW = container?.offsetWidth  ?? window.innerWidth;
      const cH = container?.offsetHeight ?? window.innerHeight;
      setWarningPopupPos(clampPopupPos(e.point.x, e.point.y, cW, cH));
      setWarningExpanded(false);
      setWarningNow(Date.now());
      // Mobile: bottom sheet with all warnings at tapped location
      setSelectedWarnings(matched);
      setWarnSheetOpen(true);
    };
    map.on("mousemove", "warnings-fill", onEnter);
    map.on("mouseleave", "warnings-fill", onLeave);
    map.on("click",      "warnings-fill", onWarnClick);
    return () => {
      map.off("mousemove", "warnings-fill", onEnter);
      map.off("mouseleave", "warnings-fill", onLeave);
      map.off("click",      "warnings-fill", onWarnClick);
      // Clean up any residual hover state on unmount.
      if (hoveredId !== null) map.setFeatureState({ source: "warnings-source", id: hoveredId }, { hover: false });
    };
  }, [mapLoaded, activeMapTool]);

  // ── dBZ probe: track cursor position ─────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map || activeMapTool !== "dbz") {
      setDbzProbeInfo(null);
      return;
    }
    const onMove = async (e) => {
      const { x, y } = e.point;
      const { lng, lat } = e.lngLat;
      const dbz = await sampleDbzFromTileAsync(probeTileUrlRef.current, lng, lat);

      let distKm = null, heightKm = null, cardinal = null;
      const site = probeActiveSiteRef.current;
      if (site) {
        distKm   = haversineKm(site.lon, site.lat, lng, lat);
        heightKm = beamHeightKm(distKm);
        cardinal = bearingToCardinal(getBearing(site.lon, site.lat, lng, lat));
      }

      // Nearest METAR with hysteresis to avoid flicker at station boundaries
      const rawNearest = findNearestMetar(probeMetarDataRef.current, lng, lat);
      let metarFeature = rawNearest;
      if (rawNearest && probeMetarHystRef.current) {
        const [lLng, lLat] = probeMetarHystRef.current.geometry.coordinates;
        const [nLng, nLat] = rawNearest.geometry.coordinates;
        const lastD = haversineMeters([lng, lat], [lLng, lLat]);
        const newD  = haversineMeters([lng, lat], [nLng, nLat]);
        if (newD >= lastD - 20_000) metarFeature = probeMetarHystRef.current;
      }
      probeMetarHystRef.current = metarFeature;

      let metarWind = null;
      if (metarFeature) {
        const p = metarFeature.properties;
        const dirCard = typeof p.wdir === "number" ? bearingToCardinal(p.wdir)
                      : p.wdir === "VRB"           ? "VRB" : null;
        if (p.wspd != null || dirCard != null) {
          metarWind = { speed: p.wspd, dir: dirCard, station: p.id };
        }
      }

      setDbzProbeInfo({ x, y, lng, lat, dbz, distKm, heightKm, cardinal, metarWind });
    };
    const onLeaveCanvas = () => setDbzProbeInfo(null);
    map.on("mousemove", onMove);
    map.getCanvas().addEventListener("mouseleave", onLeaveCanvas);
    return () => {
      map.off("mousemove", onMove);
      map.getCanvas().removeEventListener("mouseleave", onLeaveCanvas);
    };
  }, [mapLoaded, activeMapTool]);

  // ── Screenshot ────────────────────────────────────────────────────────────
  const handleScreenshot = useCallback(() => {
    const canvas = mapRef.current?.getCanvas();
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a   = document.createElement("a");
    const ts  = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href     = url;
    a.download = `stormatlas-${ts}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────
  const hasFrames   = frameCount > 0;
  const currentTime = parseFrameTime(currentFrame?.url || currentFrame?.scanId || null);
  const firstTime   = playbackFrames.length > 0 ? parseFrameTime(playbackFrames[0].url || playbackFrames[0].scanId) : null;
  const lastTime    = playbackFrames.length > 0 ? parseFrameTime(playbackFrames[playbackFrames.length - 1].url || playbackFrames[playbackFrames.length - 1].scanId) : null;

  const measurementDistanceM         = measurePoints.length === 2
    ? haversineMeters(measurePoints[0], measurePoints[1]) : null;
  const formattedMeasurementDistance = formatDistance(measurementDistanceM, measurementUnit);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="rp-stage rp-state">
        <p>⚠ Mapbox token not configured.</p>
        <p className="rp-hint">Set REACT_APP_MAPBOX_TOKEN and rebuild.</p>
      </div>
    );
  }

  return (
    <section
      className="rp-stage"
      aria-label="Radar panel"
      onKeyDown={handleSectionKey}
    >
      {/* ── Mapbox map base (single or dual pane) ──
           mapContainerRef is always at fiber depth [0][0][0] so React reuses
           the same DOM node in both modes.  In single-pane the two wrapper
           divs are also rp-map (full-screen) — harmless nesting.  In
           dual-pane the left wrapper becomes rp-pane--left (50 % wide) and
           Mapbox's container correctly reports half-width on resize().        */}
      <div className={isDualPane ? "rp-dual-container" : "rp-map"}>
        <div
          className={isDualPane
            ? `rp-pane rp-pane--left${activePane === "left" ? " rp-pane--active" : ""}`
            : "rp-map"}
        >
          <div ref={mapContainerRef} className="rp-map" />
        </div>
        {isDualPane && (
          <div
            className={`rp-pane rp-pane--right${activePane === "right" ? " rp-pane--active" : ""}`}
          >
            <div ref={rightMapContainerRef} className="rp-map rp-map--placeholder" />
          </div>
        )}
      </div>

      {/* ── Drawing overlay (geo-referenced canvas, above map, below bar) ── */}
      {mapLoaded && (
        <DrawingOverlay
          ref={drawingOverlayRef}
          map={mapRef.current}
          active={isDrawMode}
          color={drawColor}
        />
      )}

      {/* ── Draw mode sub-toolbar ── */}
      {isDrawMode && (
        <div className="sa-draw-toolbar" role="toolbar" aria-label="Drawing tools">
          {[
            { value: "#ef4444", label: "Red"    },
            { value: "#facc15", label: "Yellow" },
            { value: "#38bdf8", label: "Cyan"   },
            { value: "#ffffff", label: "White"  },
          ].map((c) => (
            <button
              key={c.value}
              type="button"
              className={`sa-draw-color${drawColor === c.value ? " active" : ""}`}
              style={{ "--dc": c.value }}
              onClick={() => setDrawColor(c.value)}
              title={c.label}
              aria-label={`Draw color: ${c.label}`}
            />
          ))}
          <div className="sa-draw-sep" aria-hidden="true" />
          {/* Undo */}
          <button
            type="button"
            className="sa-draw-action"
            onClick={() => drawingOverlayRef.current?.undo()}
            title="Undo last stroke"
          >
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 8h8a4 4 0 0 1 0 8H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="4,4 4,8 8,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {/* Clear all */}
          <button
            type="button"
            className="sa-draw-action"
            onClick={() => drawingOverlayRef.current?.clear()}
            title="Clear all drawings"
          >
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <polyline points="5,7 5,17 15,17 15,7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="3" y1="7" x2="17" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M8 7V5h4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="sa-draw-sep" aria-hidden="true" />
          {/* Exit annotate mode */}
          <button
            type="button"
            className="sa-draw-action sa-draw-exit"
            onClick={() => setActiveMapTool(null)}
            title="Exit annotate mode"
          >
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <line x1="5" y1="5" x2="15" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="15" y1="5" x2="5" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Long-press site switch toast ── */}
      {siteSwitchToast && (
        <div className="sa-site-toast" role="status" aria-live="polite">{siteSwitchToast}</div>
      )}

      {/* ── Site selector toggle (top-left, next to hamburger) ── */}
      <button
        className={`sa-sites-btn${siteSelectorOpen ? " sa-sites-btn--active" : ""}`}
        onClick={() => setSiteSelectorOpen((o) => !o)}
      >
        {activeSite ? `Sites: ${activeSite}` : "Sites"}
      </button>
      {siteSelectorOpen && (
        <SiteSelector
          siteHealthDetails={siteHealthDetails}
          activeSite={activeSite}
          onSelectSite={(siteId) => {
            onRadarSiteSelect?.(siteId);
            setSelectedRadarId(siteId);
            setSiteSelectorOpen(false);
            const map = mapRef.current;
            const detail = siteHealthDetails[siteId];
            const catalogSite = !detail && RADAR_SITES_CATALOG.find((s) => s.id === siteId);
            const lat = detail?.lat ?? catalogSite?.lat;
            const lon = detail?.lon ?? catalogSite?.lon;
            if (map && lat != null && lon != null) {
              if (window.matchMedia('(pointer: coarse)').matches) {
                if (!map.getBounds().contains([lon, lat])) {
                  map.easeTo({ center: [lon, lat], duration: 400 });
                }
              } else {
                map.flyTo({ center: [lon, lat], zoom: 7, duration: 800 });
              }
              lastFitSiteRef.current = siteId;
            }
          }}
          onClose={() => setSiteSelectorOpen(false)}
        />
      )}

      {/* ── Product legend (top-center) — hidden when idle (no site) ── */}
      {activeSite && selectedProduct === "reflectivity" && (
        <div className="sa-top-legend" aria-label="Reflectivity scale dBZ">
          <div className="sa-top-legend-labels">
            {REFLECTIVITY_LEGEND_STOPS.map((val) => (
              <span key={val} className="sa-top-legend-label">{val}</span>
            ))}
          </div>
          <div className="sa-top-legend-bar" />
        </div>
      )}
      {activeSite && selectedProduct === "velocity" && (
        <div className="sa-top-legend" aria-label="Velocity scale kt">
          <div className="sa-top-legend-labels">
            {VELOCITY_LEGEND_STOPS.map((val) => (
              <span key={val} className="sa-top-legend-label">{val > 0 ? `+${val}` : val}</span>
            ))}
          </div>
          <div className="sa-top-legend-bar sa-top-legend-bar--velocity" />
        </div>
      )}

      {/* ── Compact inline status banners (non-blocking) ── */}
      {!activeSite && !manifest && (
        <div className="rp-state" role="status">
          <p>Select a radar site to begin</p>
          <p className="rp-hint">Click a site marker on the map or long-press anywhere</p>
        </div>
      )}
      {(() => {
        // Single priority-ordered status pill — only one renders at a time.
        let type = null, msg = null, retry = null;
        if      (error && !manifest)                                            { type = 'error';   msg = 'Unable to connect to radar';  retry = onRetry; }
        else if (loading && !manifest && activeSite)                            { type = 'loading'; msg = 'Connecting to radar…'; }
        else if (selectedProduct === 'mrms' && mrmsError && !mrmsManifest)      { type = 'error';   msg = 'MRMS unavailable';            retry = retryMrms; }
        else if (selectedProduct === 'mrms' && mrmsLoading && !mrmsManifest)    { type = 'loading'; msg = 'Loading MRMS…'; }
        else if (selectedProduct !== 'mrms' && radarLoadState === 'error')      { type = 'error';   msg = 'Failed to load radar frames'; }
        else if (selectedProduct !== 'mrms' && radarLoadState === 'loading' && activeSite) { type = 'loading'; msg = 'Loading radar frames…'; }
        else if (selectedProduct !== 'mrms' && radarLoadState === 'empty')      { type = 'info';    msg = 'No frames available for this site'; }
        if (!type) return null;
        return (
          <div className={`rp-state${type === 'error' ? ' rp-state--error' : ''}`}
               role={type === 'error' ? 'alert' : 'status'}>
            {type === 'loading' && <span className="rp-spinner rp-spinner--sm" aria-hidden="true" />}
            <p>{msg}</p>
            {retry && <button type="button" className="rp-retry-btn" onClick={retry}>Retry</button>}
          </div>
        );
      })()}
      {selectedProduct === "velocity" && vel.loading && !vel.manifest && (
        <div className="rp-state" role="status">
          <span className="rp-spinner rp-spinner--sm" aria-hidden="true" />
          <p>Loading velocity…</p>
        </div>
      )}
      {selectedProduct === "velocity" && vel.error && !vel.manifest && (
        <div className="rp-state rp-state--error" role="alert">
          <p>Velocity unavailable</p>
          <button type="button" className="rp-retry-btn" onClick={vel.retry}>Retry</button>
        </div>
      )}
      {activeManifest && !hasFrames && (
        <div className={`rp-state${siteStatus === "offline" ? " rp-state--error" : ""}`} role="status">
          {siteStatus === "offline" ? (
            <p>Site offline — no recent frames</p>
          ) : (
            <>
              <span className="rp-spinner rp-spinner--sm" aria-hidden="true" />
              <p>{siteStatus === "initializing" ? "Radar initializing…" : "Waiting for frames…"}</p>
              {siteStatus === "stale" && <p className="rp-hint">Feed is delayed</p>}
            </>
          )}
        </div>
      )}

{/* ── Settings button + dropdown (top-left) ── */}
      {onSwitchView && (
        <>
          {/* Hamburger button */}
          <button
            className={`rp-switch-btn${isSettingsOpen ? " rp-switch-btn--active" : ""}`}
            onClick={() => setIsSettingsOpen((o) => {
              if (o) { setIsInfoOpen(false); setOpenSection(null); }
              return !o;
            })}
            title="Menu"
            aria-label={isSettingsOpen ? "Close menu" : "Open menu"}
            aria-expanded={isSettingsOpen}
          >
            <span className="sa-hamburger" aria-hidden="true">
              <span className="sa-hamburger-bar" />
              <span className="sa-hamburger-bar" />
              <span className="sa-hamburger-bar" />
            </span>
          </button>

          {/* Dropdown panel — always rendered, visibility controlled via CSS */}
          <div
            className={`sa-settings-dropdown${isSettingsOpen ? " sa-settings-dropdown--open" : ""}`}
            role="dialog"
            aria-label="StormAtlas menu"
            aria-hidden={!isSettingsOpen}
          >
            <div className="sa-settings-header">StormAtlas</div>

            {/* ── Group: General ── */}
            <div className="sa-settings-group-label">General</div>

            {/* ── 1. Layers ── */}
            <button
              type="button"
              className={`sa-settings-item sa-settings-submenu-toggle${openSection === "layers" ? " sa-settings-submenu-toggle--open" : ""}`}
              onClick={() => toggleSection("layers")}
              aria-expanded={openSection === "layers"}
            >
              <span className="sa-settings-item-icon" aria-hidden="true">
                <svg viewBox="0 0 14 14" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <line x1="2" y1="4" x2="12" y2="4" />
                  <line x1="2" y1="7" x2="12" y2="7" />
                  <line x1="2" y1="10" x2="12" y2="10" />
                </svg>
              </span>
              <span className="sa-settings-item-label">Layers</span>
              <span className={`sa-settings-submenu-chevron${openSection === "layers" ? " sa-settings-submenu-chevron--open" : ""}`}>
                <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
              </span>
            </button>
            <div className={`sa-settings-submenu-body${openSection === "layers" ? " sa-settings-submenu-body--open" : ""}`}>
              {/* ── Severe Weather: per-type toggle + color picker ── */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isWarningsOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsWarningsOpen((o) => !o)}
                aria-expanded={isWarningsOpen}
              >
                <span className="sa-settings-nested-label">Severe Weather</span>
                <span className={`sa-settings-submenu-chevron${isWarningsOpen ? " sa-settings-submenu-chevron--open" : ""}`}>
                  <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
                </span>
              </button>
              <div className={`sa-settings-submenu-body${isWarningsOpen ? " sa-settings-submenu-body--open" : ""}`}>
                {/* Bulk actions */}
                <div className="sa-warn-bulk-row">
                  <button
                    type="button"
                    className="sa-warn-bulk-btn"
                    onClick={() => setWarningSettings((s) => Object.fromEntries(WARNING_TYPES.map((t) => [t.key, { ...s[t.key], enabled: true }])))}
                  >Enable all</button>
                  <button
                    type="button"
                    className="sa-warn-bulk-btn"
                    onClick={() => setWarningSettings((s) => Object.fromEntries(WARNING_TYPES.map((t) => [t.key, { ...s[t.key], enabled: false }])))}
                  >Disable all</button>
                  <button
                    type="button"
                    className="sa-warn-bulk-btn sa-warn-bulk-btn--reset"
                    onClick={() => setWarningSettings(WARNING_DEFAULTS)}
                  >Reset</button>
                </div>
                {/* Per-type rows */}
                {WARNING_TYPES.map((wt) => {
                  const cfg       = warningSettings[wt.key] ?? WARNING_DEFAULTS[wt.key];
                  const isExp     = expandedWarning === wt.key;
                  const toggle    = () => setWarningSettings((s) => {
                    const curr = s[wt.key] ?? WARNING_DEFAULTS[wt.key];
                    return { ...s, [wt.key]: { ...curr, enabled: !curr.enabled } };
                  });
                  return (
                    <div key={wt.key}>
                      <div className="sa-settings-fronts-row">
                        <button
                          type="button"
                          className={`sa-settings-nested-toggle${isExp ? " sa-settings-nested-toggle--open" : ""}`}
                          onClick={() => setExpandedWarning((k) => (k === wt.key ? null : wt.key))}
                          aria-expanded={isExp}
                          style={{ flex: 1 }}
                        >
                          <span className="sa-warn-dot" style={{ background: cfg.color }} />
                          <span className="sa-settings-nested-label">{wt.label}</span>
                          <span className={`sa-settings-submenu-chevron${isExp ? " sa-settings-submenu-chevron--open" : ""}`}>
                            <svg viewBox="0 0 10 6" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
                          </span>
                        </button>
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                        <span
                          className={`sa-fronts-toggle${cfg.enabled ? " sa-fronts-toggle--on" : ""}`}
                          role="switch"
                          tabIndex={0}
                          aria-checked={cfg.enabled}
                          aria-label={cfg.enabled ? `Hide ${wt.label}` : `Show ${wt.label}`}
                          onClick={toggle}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
                        >
                          <span className="sa-fronts-toggle-thumb" />
                        </span>
                      </div>
                      {isExp && (
                        <div className="sa-warn-color-row">
                          <span className="sa-warn-color-label">Polygon color</span>
                          <label className="sa-warn-color-swatch" aria-label={`${wt.label} color`}>
                            <span className="sa-warn-color-preview" style={{ background: cfg.color }} />
                            <input
                              type="color"
                              value={cfg.color}
                              onChange={(e) => setWarningSettings((s) => {
                                const curr = s[wt.key] ?? WARNING_DEFAULTS[wt.key];
                                return { ...s, [wt.key]: { ...curr, color: e.target.value } };
                              })}
                              className="sa-warn-color-input"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Chaser Network — expandable with Spotters + Reports toggles inside */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isChaserNetworkOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsChaserNetworkOpen((o) => !o)}
                aria-expanded={isChaserNetworkOpen}
              >
                <span className="sa-settings-nested-label">Chaser Network</span>
                <span className={`sa-settings-submenu-chevron${isChaserNetworkOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
              </button>
              <div className={`sa-settings-submenu-body${isChaserNetworkOpen ? " sa-settings-submenu-body--open" : ""}`}>
                {/* Spotters toggle */}
                <div className="sa-settings-fronts-row">
                  <span className="sa-settings-nested-label" style={{ flex: 1 }}>Spotters</span>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                  <span
                    className={`sa-fronts-toggle${spotterEnabled ? " sa-fronts-toggle--on" : ""}`}
                    role="switch"
                    tabIndex={0}
                    aria-checked={spotterEnabled}
                    aria-label={spotterEnabled ? "Hide spotters" : "Show spotters"}
                    onClick={() => setSpotterEnabled((v) => !v)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSpotterEnabled((v) => !v); } }}
                  >
                    <span className="sa-fronts-toggle-thumb" />
                  </span>
                </div>
                {/* Reports — toggle + source/type filter chips */}
                <div className="sa-settings-fronts-row">
                  <span className="sa-settings-nested-label" style={{ flex: 1 }}>Reports</span>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                  <span
                    className={`sa-fronts-toggle${reportsEnabled ? " sa-fronts-toggle--on" : ""}`}
                    role="switch"
                    tabIndex={0}
                    aria-checked={reportsEnabled}
                    aria-label={reportsEnabled ? "Hide reports" : "Show reports"}
                    onClick={() => setReportsEnabled((v) => !v)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setReportsEnabled((v) => !v); } }}
                  >
                    <span className="sa-fronts-toggle-thumb" />
                  </span>
                </div>
                {reportsEnabled && (
                  <>
                    <div className="sa-report-filter-row">
                      {["mping", "spotter", "stormatlas"].map((src) => (
                        <button
                          key={src}
                          type="button"
                          className={`sa-report-chip${reportSources.includes(src) ? " sa-report-chip--on" : ""}`}
                          onClick={() => setReportSources((prev) =>
                            prev.includes(src) ? prev.filter((s) => s !== src) : [...prev, src]
                          )}
                        >
                          {REPORT_SOURCES[src].label}
                        </button>
                      ))}
                    </div>
                    <div className="sa-report-filter-row">
                      {Object.entries(REPORT_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          type="button"
                          className={`sa-report-chip${reportTypes.includes(key) ? " sa-report-chip--on" : ""}`}
                          style={reportTypes.includes(key) ? { "--chip-color": cfg.color } : {}}
                          onClick={() => setReportTypes((prev) =>
                            prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
                          )}
                        >
                          {cfg.abbr}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Mesoscale Discussions — toggle in header + expandable filters */}
              <div className="sa-settings-fronts-row">
                <button
                  type="button"
                  className={`sa-settings-nested-toggle${isMesoscaleOpen ? " sa-settings-nested-toggle--open" : ""}`}
                  onClick={() => setIsMesoscaleOpen((o) => !o)}
                  aria-expanded={isMesoscaleOpen}
                >
                  <span className="sa-settings-nested-label">Mesoscale Discussions</span>
                </button>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                <span
                  className={`sa-fronts-toggle${mdEnabled ? " sa-fronts-toggle--on" : ""}`}
                  role="switch" tabIndex={0} aria-checked={mdEnabled}
                  aria-label={mdEnabled ? "Disable MD overlay" : "Enable MD overlay"}
                  onClick={(e) => { e.stopPropagation(); setMdEnabled((v) => !v); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMdEnabled((v) => !v); } }}
                ><span className="sa-fronts-toggle-thumb" /></span>
                <button
                  type="button"
                  className="sa-fronts-chevron-btn"
                  onClick={() => setIsMesoscaleOpen((o) => !o)}
                  aria-label="Toggle mesoscale filters"
                >
                  <span className={`sa-settings-submenu-chevron${isMesoscaleOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
                </button>
              </div>
              <div className={`sa-settings-submenu-body${isMesoscaleOpen ? " sa-settings-submenu-body--open" : ""}`}>
                {[["severe", "Severe"], ["flood", "Flood"], ["winter", "Winter"]].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className="sa-settings-subitem"
                    onClick={() => setMdFilters((f) => ({ ...f, [key]: !f[key] }))}
                  >
                    <span className={`sa-settings-subitem-dot${mdFilters[key] ? " sa-settings-subitem-dot--on" : ""}`} />
                    <span className="sa-settings-subitem-label">{label}</span>
                  </button>
                ))}
              </div>
              {mdEnabled && mdData !== null && mdData.features.length === 0 && (
                <div className="sa-md-empty-note">No active Mesoscale Discussions</div>
              )}

              {/* MRMS — expandable */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isMrmsOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsMrmsOpen((o) => !o)}
                aria-expanded={isMrmsOpen}
              >
                <span className="sa-settings-nested-label">MRMS</span>
                <span className={`sa-settings-submenu-chevron${isMrmsOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
              </button>
              <div className={`sa-settings-submenu-body${isMrmsOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <button type="button" className="sa-settings-subitem" disabled>
                  <span className="sa-settings-subitem-dot" />
                  <span className="sa-settings-subitem-label">Coming soon</span>
                  <span className="sa-settings-subitem-badge">SOON</span>
                </button>
              </div>

              {/* METARs — toggle in header row */}
              <div className="sa-settings-fronts-row">
                <span className="sa-settings-nested-label" style={{ flex: 1 }}>METARs</span>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                <span
                  className={`sa-fronts-toggle${metarEnabled ? " sa-fronts-toggle--on" : ""}`}
                  role="switch"
                  tabIndex={0}
                  aria-checked={metarEnabled}
                  aria-label={metarEnabled ? "Hide METARs" : "Show METARs"}
                  onClick={() => setMetarEnabled((v) => !v)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMetarEnabled((v) => !v); } }}
                >
                  <span className="sa-fronts-toggle-thumb" />
                </span>
              </div>

              {/* Fronts — toggle + expandable legend */}
              <div className="sa-settings-fronts-row">
                <button
                  type="button"
                  className={`sa-settings-nested-toggle${isFrontsOpen ? " sa-settings-nested-toggle--open" : ""}`}
                  onClick={() => setIsFrontsOpen((o) => !o)}
                  aria-expanded={isFrontsOpen}
                >
                  <span className="sa-settings-nested-label">Fronts</span>
                  {showFronts && frontsError && (
                    <span className="sa-fronts-unavail" title="NOAA fronts server unavailable">⚠</span>
                  )}
                </button>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                <span
                  className={`sa-fronts-toggle${showFronts ? " sa-fronts-toggle--on" : ""}`}
                  role="switch"
                  tabIndex={0}
                  aria-checked={showFronts}
                  aria-label={showFronts ? "Hide fronts" : "Show fronts"}
                  onClick={(e) => { e.stopPropagation(); setShowFronts((v) => !v); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowFronts((v) => !v); } }}
                >
                  <span className="sa-fronts-toggle-thumb" />
                </span>
                <button
                  type="button"
                  className="sa-fronts-chevron-btn"
                  onClick={() => setIsFrontsOpen((o) => !o)}
                  aria-label="Toggle fronts legend"
                >
                  <span className={`sa-settings-submenu-chevron${isFrontsOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
                </button>
              </div>
              <div className={`sa-settings-submenu-body${isFrontsOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <div className="sa-fronts-legend">
                  <div className="sa-fronts-legend-item">
                    <svg className="sa-fronts-swatch-svg" viewBox="0 0 28 14" aria-hidden="true">
                      <line x1="0" y1="10" x2="28" y2="10" stroke="#3b82f6" strokeWidth="2.5" />
                      <polygon points="14,2 10,10 18,10" fill="#3b82f6" />
                    </svg>
                    <span>Cold Front</span>
                  </div>
                  <div className="sa-fronts-legend-item">
                    <svg className="sa-fronts-swatch-svg" viewBox="0 0 28 14" aria-hidden="true">
                      <line x1="0" y1="10" x2="28" y2="10" stroke="#ef4444" strokeWidth="2.5" />
                      <path d="M8,10 A6,6 0 0,1 20,10" fill="#ef4444" />
                    </svg>
                    <span>Warm Front</span>
                  </div>
                  <div className="sa-fronts-legend-item">
                    <svg className="sa-fronts-swatch-svg" viewBox="0 0 28 14" aria-hidden="true">
                      <line x1="0" y1="10" x2="28" y2="10" stroke="#a855f7" strokeWidth="2.5" />
                      <path d="M8,10 A6,6 0 0,1 20,10" fill="#a855f7" />
                    </svg>
                    <span>Stationary Front</span>
                  </div>
                  <div className="sa-fronts-legend-item">
                    <svg className="sa-fronts-swatch-svg" viewBox="0 0 28 14" aria-hidden="true">
                      <line x1="0" y1="10" x2="28" y2="10" stroke="#7c3aed" strokeWidth="2.5" />
                      <polygon points="14,2 10,10 18,10" fill="#7c3aed" />
                    </svg>
                    <span>Occluded Front</span>
                  </div>
                  <div className="sa-fronts-legend-item"><span className="sa-fronts-swatch sa-fronts-swatch--dashed" style={{borderColor:"#f59e0b"}} /><span>Trough</span></div>
                  <div className="sa-fronts-legend-item"><span className="sa-fronts-swatch sa-fronts-swatch--dashed" style={{borderColor:"#6b7280"}} /><span>Outflow Boundary</span></div>
                  <div className="sa-fronts-legend-item"><span className="sa-fronts-swatch sa-fronts-swatch--dashed" style={{borderColor:"#f43f5e"}} /><span>Squall Line</span></div>
                  <div className="sa-fronts-legend-item"><span className="sa-fronts-swatch sa-fronts-swatch--dashed" style={{borderColor:"#d97706"}} /><span>Dry Line</span></div>
                </div>
              </div>
            </div>

            {/* ── 3. Tools ── */}
            <div className="sa-settings-divider" />
            <button
              type="button"
              className={`sa-settings-item sa-settings-submenu-toggle${openSection === "tools" ? " sa-settings-submenu-toggle--open" : ""}`}
              onClick={() => toggleSection("tools")}
              aria-expanded={openSection === "tools"}
            >
              <span className="sa-settings-item-icon" aria-hidden="true">
                <svg viewBox="0 0 14 14" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="2" y1="12" x2="9" y2="5" />
                  <path d="M9 2 L12 5 L11 8 L9 5 L6 3 Z" />
                </svg>
              </span>
              <span className="sa-settings-item-label">Tools</span>
              <span className={`sa-settings-submenu-chevron${openSection === "tools" ? " sa-settings-submenu-chevron--open" : ""}`}>
                <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
              </span>
            </button>
            <div className={`sa-settings-submenu-body${openSection === "tools" ? " sa-settings-submenu-body--open" : ""}`}>
              {/* Ruler — expandable */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isPlaybackSettingsOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsPlaybackSettingsOpen((o) => !o)}
                aria-expanded={isPlaybackSettingsOpen}
              >
                <span className="sa-settings-nested-label">Ruler</span>
                <span className={`sa-settings-submenu-chevron${isPlaybackSettingsOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
              </button>
              <div className={`sa-settings-submenu-body${isPlaybackSettingsOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <div className="sa-settings-pref-row">
                  <label className="sa-settings-pref-label" htmlFor="sa-pref-ruler-unit">Distance Units</label>
                  <select
                    id="sa-pref-ruler-unit"
                    className="sa-settings-pref-select"
                    value={measurementUnit}
                    onChange={(e) => handleRulerUnitChange(e.target.value)}
                  >
                    <option value="miles">Miles</option>
                    <option value="yards">Yards</option>
                    <option value="meters">Meters</option>
                    <option value="kilometers">Kilometers</option>
                    <option value="nautical">Nautical miles</option>
                  </select>
                </div>
                <div className="sa-settings-pref-row">
                  <label className="sa-settings-pref-label" htmlFor="sa-pref-ruler-behavior">Unit Selection</label>
                  <select
                    id="sa-pref-ruler-behavior"
                    className="sa-settings-pref-select"
                    value={rulerBehavior}
                    onChange={(e) => handleRulerBehaviorChange(e.target.value)}
                  >
                    <option value="ask">Ask each time</option>
                    <option value="remember">Remember last used</option>
                  </select>
                </div>
              </div>

              {/* ── dBZ Probe ── */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isDbzSettingsOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsDbzSettingsOpen((o) => !o)}
                aria-expanded={isDbzSettingsOpen}
              >
                <span className="sa-settings-nested-label">dBZ Probe</span>
                <span className={`sa-settings-submenu-chevron${isDbzSettingsOpen ? " sa-settings-submenu-chevron--open" : ""}`}>
                  <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
                </span>
              </button>
              <div className={`sa-settings-submenu-body${isDbzSettingsOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <div className="sa-settings-pref-row">
                  <label className="sa-settings-pref-label" htmlFor="sa-pref-dbz-dist">Distance Unit</label>
                  <select
                    id="sa-pref-dbz-dist"
                    className="sa-settings-pref-select"
                    value={dbzDistUnit}
                    onChange={(e) => setDbzDistUnit(e.target.value)}
                  >
                    <option value="km">Kilometers</option>
                    <option value="mi">Miles</option>
                    <option value="nm">Nautical miles</option>
                    <option value="ft">Feet</option>
                    <option value="yd">Yards</option>
                  </select>
                </div>
                <div className="sa-settings-pref-row">
                  <label className="sa-settings-pref-label" htmlFor="sa-pref-dbz-alt">Altitude Unit</label>
                  <select
                    id="sa-pref-dbz-alt"
                    className="sa-settings-pref-select"
                    value={dbzAltUnit}
                    onChange={(e) => setDbzAltUnit(e.target.value)}
                  >
                    <option value="km">Kilometers</option>
                    <option value="mi">Miles</option>
                    <option value="nm">Nautical miles</option>
                    <option value="ft">Feet</option>
                    <option value="yd">Yards</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ── 4. O.R.A. ── */}
            <div className="sa-settings-divider" />
            <button
              type="button"
              className={`sa-settings-item sa-settings-submenu-toggle${openSection === "ora" ? " sa-settings-submenu-toggle--open" : ""}`}
              onClick={() => toggleSection("ora")}
              aria-expanded={openSection === "ora"}
            >
              <span className="sa-settings-item-icon" aria-hidden="true">
                <svg viewBox="0 0 14 14" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="7" cy="7" r="5" />
                  <circle cx="7" cy="7" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <span className="sa-settings-item-label">O.R.A.</span>
              <span className={`sa-settings-submenu-chevron${openSection === "ora" ? " sa-settings-submenu-chevron--open" : ""}`}>
                <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
              </span>
            </button>
            <div className={`sa-settings-submenu-body${openSection === "ora" ? " sa-settings-submenu-body--open" : ""}`}>
              <div style={{ padding: "6px 14px 10px", color: "#64748b", fontSize: "12px", lineHeight: 1.5 }}>
                O.R.A. is active in the top-right panel.
              </div>
            </div>

            {/* ── Group: Settings ── */}
            <div className="sa-settings-group-label">Settings</div>

            {/* ── 5. App Settings ── */}
            <button
              type="button"
              className={`sa-settings-item sa-settings-submenu-toggle${openSection === "app" ? " sa-settings-submenu-toggle--open" : ""}`}
              onClick={() => toggleSection("app")}
              aria-expanded={openSection === "app"}
            >
              <span className="sa-settings-item-icon" aria-hidden="true">
                <svg viewBox="0 0 14 14" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="7" cy="7" r="2" />
                  <path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.9 2.9l1.05 1.05M10.05 10.05l1.05 1.05M2.9 11.1l1.05-1.05M10.05 3.95l1.05-1.05" />
                </svg>
              </span>
              <span className="sa-settings-item-label">App Settings</span>
              <span className={`sa-settings-submenu-chevron${openSection === "app" ? " sa-settings-submenu-chevron--open" : ""}`}>
                <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
              </span>
            </button>
            <div className={`sa-settings-submenu-body${openSection === "app" ? " sa-settings-submenu-body--open" : ""}`}>
              {/* Map Style — expandable */}
              <button
                type="button"
                className={`sa-settings-nested-toggle${isMapStyleOpen ? " sa-settings-nested-toggle--open" : ""}`}
                onClick={() => setIsMapStyleOpen((o) => !o)}
                aria-expanded={isMapStyleOpen}
              >
                <span className="sa-settings-nested-label">Map</span>
                <span className={`sa-settings-submenu-chevron${isMapStyleOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
              </button>
              <div className={`sa-settings-submenu-body sa-settings-submenu-body--map${isMapStyleOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <div className="sa-map-style-list">
                  {MAP_STYLES.map((s) => {
                    const stylePath = s.url.replace("mapbox://styles/", "");
                    const thumbUrl = `https://api.mapbox.com/styles/v1/${stylePath}/static/-96,38.5,3/120x80@2x?access_token=${MAPBOX_TOKEN}`;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`sa-map-style-row${s.id === mapStyleId ? " sa-map-style-row--active" : ""}`}
                        onClick={() => handleMapStyleChange(s.id)}
                      >
                        <img
                          className="sa-map-style-thumb"
                          src={thumbUrl}
                          alt={s.label}
                          loading="lazy"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <span className="sa-map-style-label">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── 6. Account ── */}
            <div className="sa-settings-divider" />
            <button
              type="button"
              className={`sa-settings-item sa-settings-submenu-toggle${openSection === "account" ? " sa-settings-submenu-toggle--open" : ""}`}
              onClick={() => toggleSection("account")}
              aria-expanded={openSection === "account"}
            >
              <span className="sa-settings-item-icon" aria-hidden="true">
                <svg viewBox="0 0 14 14" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="7" cy="5" r="2.5" />
                  <path d="M2 13c0-2.76 2.24-5 5-5s5 2.24 5 5" />
                </svg>
              </span>
              <span className="sa-settings-item-label">Account</span>
              <span className={`sa-settings-submenu-chevron${openSection === "account" ? " sa-settings-submenu-chevron--open" : ""}`}>
                <svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg>
              </span>
            </button>
            <div className={`sa-settings-submenu-body${openSection === "account" ? " sa-settings-submenu-body--open" : ""}`}>
              <button type="button" className="sa-settings-subitem" disabled>
                <span className="sa-settings-subitem-dot" />
                <span className="sa-settings-subitem-label">Manage Subscription</span>
                <span className="sa-settings-subitem-badge">SOON</span>
              </button>
              <button type="button" className="sa-settings-subitem" disabled>
                <span className="sa-settings-subitem-dot" />
                <span className="sa-settings-subitem-label">Chaser Network</span>
                <span className="sa-settings-subitem-badge">SOON</span>
              </button>
              <button type="button" className="sa-settings-subitem" disabled>
                <span className="sa-settings-subitem-dot" />
                <span className="sa-settings-subitem-label">Location History</span>
                <span className="sa-settings-subitem-badge">SOON</span>
              </button>
              <button type="button" className="sa-settings-subitem" disabled>
                <span className="sa-settings-subitem-dot" />
                <span className="sa-settings-subitem-label">Locations</span>
                <span className="sa-settings-subitem-badge">SOON</span>
              </button>
            </div>

            {/* ── Support, Bugs, Info — footer (outside accordion) ── */}
            <div className="sa-settings-footer">
              <div className="sa-settings-divider" />
              <button
                type="button"
                className={`sa-settings-item sa-settings-submenu-toggle${isInfoOpen ? " sa-settings-submenu-toggle--open" : ""}`}
                onClick={() => setIsInfoOpen((o) => !o)}
                aria-expanded={isInfoOpen}
              >
                <span className="sa-settings-item-label">Support, Bugs, Info</span>
                <span className={`sa-settings-submenu-chevron${isInfoOpen ? " sa-settings-submenu-chevron--open" : ""}`}><svg viewBox="0 0 10 6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1L5 5L9 1"/></svg></span>
              </button>
              <div className={`sa-settings-submenu-body${isInfoOpen ? " sa-settings-submenu-body--open" : ""}`}>
                <button type="button" className="sa-settings-subitem" disabled>
                  <span className="sa-settings-subitem-dot" />
                  <span className="sa-settings-subitem-label">Future Updates</span>
                  <span className="sa-settings-subitem-badge">SOON</span>
                </button>
                <button type="button" className="sa-settings-subitem" disabled>
                  <span className="sa-settings-subitem-dot" />
                  <span className="sa-settings-subitem-label">Report a Bug</span>
                  <span className="sa-settings-subitem-badge">SOON</span>
                </button>
                <button type="button" className="sa-settings-subitem" disabled>
                  <span className="sa-settings-subitem-dot" />
                  <span className="sa-settings-subitem-label">Support</span>
                  <span className="sa-settings-subitem-badge">SOON</span>
                </button>
              </div>
            </div>

          </div>
        </>
      )}

      {/* ── O.R.A. overlay card (top-right) ── */}
      <ORAOverlayCard
        apiBase={API_BASE}
        site={activeManifest?.site ?? null}
        frameCount={frameCount}
        onWarningFocus={handleWarningFocus}
        nearbyCenter={nearbyCenter}
        onWarningsUpdate={setActiveWarnings}
      />

      {/* ── Playback bar (bottom-center) ── */}
      {/* Always visible so users can select a product before choosing a site.
          In NEXRAD mode the bar reflects System 1 (RadarMapRenderer) state.
          In MRMS mode it reflects System 2 (RadarPlaybackEngine) state.     */}
      <RadarPlaybackBar
        playing={selectedProduct !== "mrms" ? radarPlaying    : playing}
        frameIndex={selectedProduct !== "mrms" ? radarFrameIdx   : frameIndex}
        frameCount={selectedProduct !== "mrms" ? radarFrameCount : frameCount}
        currentTime={selectedProduct !== "mrms" ? radarTimestamp  : currentTime}
        firstTime={selectedProduct !== "mrms" ? null             : firstTime}
        lastTime={selectedProduct !== "mrms" ? null             : lastTime}
        liveMode={selectedProduct !== "mrms" ? true             : liveMode}
        siteStatus={!activeSite ? "offline" : selectedProduct !== "mrms" && selectedProduct !== "velocity" ? "online" : siteStatus}
        siteLabel={selectedProduct !== "mrms" ? (activeSite ?? null) : (activeManifest?.site ?? null)}
        onPlayPause={selectedProduct !== "mrms" ? handleRadarPlayPause : handlePlayPause}
        onPrev={selectedProduct !== "mrms" ? handleRadarPrev   : handlePrev}
        onNext={selectedProduct !== "mrms" ? handleRadarNext   : handleNext}
        onSeek={selectedProduct !== "mrms" ? handleRadarSeek   : handleSeek}
        onLatest={selectedProduct !== "mrms" ? handleRadarLatest : handleLatest}
        productSlot={
          <div className="sa-playback-chips-row">
            <div className="sa-playback-product-anchor">
              <button
                type="button"
                className={`sa-playback-product-chip${isProductsMenuOpen ? " open" : ""}`}
                onClick={() => { setIsProductsMenuOpen((prev) => !prev); setIsFramesMenuOpen(false); }}
                aria-expanded={isProductsMenuOpen}
                aria-haspopup="listbox"
                aria-label="Toggle radar products menu"
              >
                <span className="sa-playback-product-chip-label">
                  {isDualPane ? activePaneShortLabel : selectedProductShortLabel}
                </span>
              </button>

              {isProductsMenuOpen && (
                <div className="sa-playback-product-menu" role="listbox" aria-label="Radar products">
                  <div className="sa-playback-product-menu-header">
                    {isDualPane
                      ? `${activePane === "left" ? "Left" : "Right"} — ${activePaneLabel}`
                      : selectedProductLabel}
                  </div>
                  <div className="sa-playback-product-menu-scroll">
                    {RADAR_PRODUCT_GROUPS.map(({ group, items }) => (
                      <div className="sa-products-group" key={group}>
                        <div className="sa-products-group-title">{group}</div>
                        <div className="sa-products-list">
                          {items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              role="option"
                              aria-selected={activePaneProduct === item.id}
                              className={`sa-product-item${activePaneProduct === item.id ? " selected" : ""}`}
                              onClick={() => {
                                if (isDualPane && activePane === "right") {
                                  setRightProduct(item.id);
                                } else {
                                  setSelectedProduct(item.id);
                                }
                                setIsProductsMenuOpen(false);
                              }}
                            >
                              <span className="sa-product-item-main">
                                <span className="sa-product-item-text">
                                  <span className="sa-product-item-label">{item.label}</span>
                                </span>
                                <span className="sa-product-item-meta">
                                  {item.badges?.map((b) => <span key={b} className="sa-product-badge">{b}</span>)}
                                  {item.tilt && <span className="sa-product-pill">{item.tilt}</span>}
                                  {!item.active && <span className="sa-product-pill placeholder">Coming Soon</span>}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Frames count chip ── */}
            <div className="sa-playback-product-anchor">
              <button
                type="button"
                className={`sa-playback-product-chip${isFramesMenuOpen ? " open" : ""}`}
                onClick={() => { setIsFramesMenuOpen((prev) => !prev); setIsProductsMenuOpen(false); }}
                aria-expanded={isFramesMenuOpen}
                aria-haspopup="listbox"
                aria-label="Toggle frames menu"
              >
                <span className="sa-playback-product-chip-label">{frameLimit}F</span>
              </button>
              {isFramesMenuOpen && (
                <div className="sa-playback-product-menu sa-frames-menu" role="listbox" aria-label="Frame count">
                  <div className="sa-playback-product-menu-header">Frames to load</div>
                  {[10, 14, 18, 24, 30].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="option"
                      aria-selected={frameLimit === n}
                      className={`sa-product-item${frameLimit === n ? " selected" : ""}`}
                      onClick={() => { setFrameLimit(n); setIsFramesMenuOpen(false); }}
                    >
                      <span className="sa-product-item-main">
                        <span className="sa-product-item-text">
                          <span className="sa-product-item-label">{n} frames</span>
                        </span>
                        {n === 10 && (
                          <span className="sa-product-item-meta">
                            <span className="sa-product-pill">Default</span>
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
        iconsSlot={
          <>
            <div className="sa-tool-divider" aria-hidden="true" />

            {/* Ruler */}
            <div className="sa-ruler-anchor">
              <button
                type="button"
                className={`sa-measure-toggle${isMeasureMode ? " active" : ""}`}
                onClick={() => {
                  const next = activeMapTool !== "measure";
                  if (next) {
                    setDbzProbeInfo(null);
                    setActiveMapTool("measure");
                    setIsMeasureUnitOpen(true);
                  } else {
                    setActiveMapTool(null);
                    setIsMeasureUnitOpen(false);
                    setMeasurePoints([]);
                    setMeasureLabelPx(null);
                    setMeasureFinalized(false);
                    if (mapRef.current) resetMeasureLayers(mapRef.current);
                  }
                }}
                aria-pressed={isMeasureMode}
                title={isMeasureMode ? "Exit measure mode" : "Measure distance on map"}
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <rect x="2" y="7" width="16" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="5"  y1="7" x2="5"  y2="10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  <line x1="8"  y1="7" x2="8"  y2="9"  stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  <line x1="11" y1="7" x2="11" y2="9"  stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  <line x1="14" y1="7" x2="14" y2="10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Draw */}
            <button
              type="button"
              className={`sa-measure-toggle${isDrawMode ? " active" : ""}`}
              onClick={() => {
                if (activeMapTool === "draw") {
                  setActiveMapTool(null);
                } else {
                  setDbzProbeInfo(null);
                  setMeasurePoints([]);
                  setMeasureFinalized(false);
                  if (mapRef.current) resetMeasureLayers(mapRef.current);
                  setActiveMapTool("draw");
                }
              }}
              aria-pressed={isDrawMode}
              title={isDrawMode ? "Exit annotate mode" : "Annotate map"}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M14.5 2.5l3 3L6 17H3v-3L14.5 2.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Report */}
            <button
              type="button"
              className={`sa-measure-toggle${isReportSheetOpen ? " active" : ""}`}
              onClick={() => setIsReportSheetOpen((v) => !v)}
              aria-pressed={isReportSheetOpen}
              title="Submit a weather report"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 3v14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M5 3.5h9l-2.5 4 2.5 4H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Radar sites */}
            <button
              type="button"
              className={`sa-radar-sites-toggle${radarSitesVisible ? " active" : ""}`}
              onClick={() => setRadarSitesVisible((v) => !v)}
              aria-pressed={radarSitesVisible}
              title={radarSitesVisible ? "Hide radar sites" : "Show radar sites"}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 11 L5.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M7.9 7.9 A3 3 0 0 1 12.1 7.9" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                <path d="M5.5 5.5 A6.4 6.4 0 0 1 14.5 5.5" stroke="currentColor" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.55" />
                <circle cx="10" cy="11" r="1.5" fill="currentColor" />
                <line x1="10" y1="12.5" x2="10" y2="17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <line x1="7.5" y1="17" x2="12.5" y2="17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>

            {/* ── Extra tools (hidden on narrow screens via .rpb-tools-extra) ── */}
            <div className="rpb-tools-extra">

              {/* Screenshot — export map canvas as PNG */}
              <button
                type="button"
                className="sa-tool-btn"
                onClick={handleScreenshot}
                aria-label="Download map screenshot"
                title="Download map screenshot"
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <rect x="2" y="4.5" width="16" height="11" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="10" cy="10" r="2.8" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M7.5 3h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </button>

              {/* Dual pane — split-view mode toggle */}
              <button
                type="button"
                className={`sa-tool-btn${isDualPane ? " active" : ""}`}
                onClick={() => {
                  setIsDualPane((v) => {
                    if (!v) {
                      // Entering dual mode: pick complementary product for right pane.
                      setRightProduct(selectedProduct === "velocity" ? "reflectivity" : "velocity");
                    } else {
                      // Exiting dual mode: reset active pane to left.
                      setActivePane("left");
                    }
                    return !v;
                  });
                }}
                aria-pressed={isDualPane}
                title={isDualPane ? "Exit dual pane" : "Dual pane view"}
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <rect x="2"  y="3.5" width="7" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                  <rect x="11" y="3.5" width="7" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                </svg>
              </button>

              {/* dBZ probe — hover to read reflectivity at cursor */}
              <button
                type="button"
                className={`sa-tool-btn${activeMapTool === "dbz" ? " active" : ""}`}
                onClick={() => {
                  const next = activeMapTool !== "dbz";
                  if (next) {
                    // Exit measure cleanly if it was active
                    if (activeMapTool === "measure") {
                      setMeasurePoints([]);
                      setMeasureLabelPx(null);
                      setMeasureFinalized(false);
                      if (mapRef.current) resetMeasureLayers(mapRef.current);
                    }
                    setActiveMapTool("dbz");
                  } else {
                    setActiveMapTool(null);
                    setDbzProbeInfo(null);
                  }
                }}
                aria-pressed={activeMapTool === "dbz"}
                title={activeMapTool === "dbz" ? "Exit dBZ probe mode" : "dBZ probe — hover to inspect reflectivity"}
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <line x1="10" y1="2"  x2="10" y2="6.5"  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <line x1="10" y1="13.5" x2="10" y2="18" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <line x1="2"  y1="10" x2="6.5"  y2="10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <line x1="13.5" y1="10" x2="18" y2="10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <circle cx="10" cy="10" r="2.8" stroke="currentColor" strokeWidth="1.3" />
                </svg>
              </button>

            </div>
          </>
        }
      />

      {/* ── Measure distance label (screen-space float) ── */}
      {measureLabelPx && formattedMeasurementDistance && (
        <div
          className="sa-measure-label"
          style={{ left: measureLabelPx.x, top: measureLabelPx.y }}
        >
          {formattedMeasurementDistance}
        </div>
      )}

      {/* ── dBZ probe tooltip (desktop only) ── */}
      {dbzProbeInfo && !isTouchDevice && (
        <div
          className="sa-dbz-probe"
          style={{ left: dbzProbeInfo.x + 14, top: dbzProbeInfo.y - 14 }}
          aria-hidden="true"
        >
          <span className="sa-dbz-probe-val">
            {dbzProbeInfo.dbz != null ? `~${dbzProbeInfo.dbz} dBZ` : "dBZ —"}
          </span>
          {dbzProbeInfo.distKm != null && (
            <span className="sa-dbz-probe-geo">
              {formatProbeDistance(dbzProbeInfo.distKm, dbzDistUnit)} {dbzProbeInfo.cardinal}
              {dbzProbeInfo.heightKm != null && ` · ${formatProbeDistance(dbzProbeInfo.heightKm, dbzAltUnit)} AGL`}
            </span>
          )}
          {dbzProbeInfo.metarWind && (
            <span className="sa-dbz-probe-wind">
              {dbzProbeInfo.metarWind.dir ? `${dbzProbeInfo.metarWind.dir} ` : ""}
              {dbzProbeInfo.metarWind.speed != null ? `${dbzProbeInfo.metarWind.speed}kt` : "—kt"}
              {` · ${dbzProbeInfo.metarWind.station}`}
            </span>
          )}
        </div>
      )}

      {/* ── Mobile dBZ probe: fixed-centre crosshair + info card ── */}
      {activeMapTool === "dbz" && isTouchDevice && mapLoaded && (
        <ProbeOverlay
          map={mapRef.current}
          tileUrlTemplate={currentFrame?.tileUrlTemplate
            ? toAbsoluteUrl(normalizeUrl(currentFrame.tileUrlTemplate))
            : null}
          activeSite={activeSiteData}
          metarData={metarData}
          distUnit={dbzDistUnit}
          altUnit={dbzAltUnit}
        />
      )}

      {/* ── Warning detail card ── */}
      {selectedWarning && warningPopupPos && (
        <div
          className="sa-warn-card"
          role="dialog"
          aria-label="Warning details"
          style={{ left: warningPopupPos.left, top: warningPopupPos.top }}
        >
          <div
            className="sa-warn-card-header"
            style={{
              borderLeftColor: (
                { tornado: "#f43f5e", storm: "#f59e0b", flood: "#3b82f6" }
                [selectedWarning.hazard_type] ?? "#94a3b8"
              ),
            }}
          >
            <span className="sa-warn-card-event">
              <span className="sa-warn-card-icon" aria-hidden="true">{getWarningIcon(selectedWarning.hazard_type)}</span>
              {selectedWarning.event || "Warning"}
            </span>
            {selectedWarning.geometry && (
              <button
                type="button"
                className="sa-warn-card-jump"
                onClick={() => handleWarningFocus(selectedWarning)}
                aria-label="Jump to warning location"
                title="Jump to location"
              >
                <svg viewBox="0 0 12 12" fill="none" width="10" height="10" aria-hidden="true">
                  <circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" fill="none" />
                  <line x1="6" y1="8" x2="6" y2="11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="sa-warn-card-close"
              onClick={() => { setSelectedWarning(null); setSelectedWarningId(null); setWarningExpanded(false); }}
              aria-label="Close warning card"
            >
              <svg viewBox="0 0 12 12" fill="none" width="10" height="10" aria-hidden="true">
                <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="sa-warn-card-meta">
            {(() => {
              const { text, urgency } = getExpiresCountdown(selectedWarning.expires_iso || selectedWarning.expires, warningNow);
              return (
                <span className={`sa-warn-card-countdown sa-warn-card-countdown--${urgency}`}>
                  {text}
                </span>
              );
            })()}
            {selectedWarning.obsType && selectedWarning.obsType !== "Not specified" && (
              <>
                <span className="sa-warn-card-sep" aria-hidden="true">·</span>
                <span className="sa-warn-card-obstype">{selectedWarning.obsType}</span>
              </>
            )}
            {!selectedWarning.geometry && (
              <>
                <span className="sa-warn-card-sep" aria-hidden="true">·</span>
                <span className="sa-warn-card-noloc">No map location</span>
              </>
            )}
          </div>

          {(() => {
            const a = parseWarnAttrs(selectedWarning.description || selectedWarning.fullText || "");
            const rows = [
              a.tornadoPossible && ["Tornado",      "Possible"],
              a.hail            && ["Hail",          a.hail],
              a.wind            && ["Wind",          a.wind],
              a.stormMotion     && ["Movement",      a.stormMotion],
              a.visibility      && ["Visibility",    a.visibility],
              a.damageThreat    && ["Damage Threat", a.damageThreat],
            ].filter(Boolean);
            return rows.length ? (
              <div className="sa-warn-card-attrs">
                {rows.map(([label, value]) => (
                  <div key={label} className="sa-warn-card-attr">
                    <span className="sa-warn-card-attr-label">{label}</span>
                    <span className="sa-warn-card-attr-value">{value}</span>
                  </div>
                ))}
              </div>
            ) : null;
          })()}

          {selectedWarning.areaDesc && (
            <div className="sa-warn-card-area">{selectedWarning.areaDesc}</div>
          )}

          {selectedWarning.fullText && (
            <button
              type="button"
              className="sa-warn-card-toggle"
              onClick={() => setWarningExpanded((v) => !v)}
            >
              {warningExpanded ? "Hide full text" : "Show full text"}
            </button>
          )}

          {warningExpanded && selectedWarning.fullText && (
            <div className="sa-warn-card-body">{selectedWarning.fullText}</div>
          )}
        </div>
      )}

      {/* ── Mesoscale Discussion popup ── */}
      {selectedMD && mdPopupPos && (
        <div className="sa-md-popup" style={{ left: mdPopupPos.left, top: mdPopupPos.top }}>
          <div className="sa-md-header">
            <span>MD #{selectedMD.id}</span>
            <button
              type="button"
              className="sa-warn-card-close"
              onClick={() => { setSelectedMD(null); setMdPopupPos(null); }}
              aria-label="Close"
            >
              <svg viewBox="0 0 12 12" fill="none" width="10" height="10" aria-hidden="true">
                <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          {selectedMD.title && <div className="sa-md-title">{selectedMD.title}</div>}
          <div className="sa-md-body">
            {selectedMD.issued_at && <div>Issued: {selectedMD.issued_at}</div>}
            {selectedMD.watch_probability != null && (
              <div>Watch Probability: {selectedMD.watch_probability}%</div>
            )}
          </div>
          <div className="sa-md-actions">
            <button
              type="button"
              onClick={() => window.open(
                `https://www.spc.noaa.gov/products/md/md${String(selectedMD.id).padStart(4, "0")}.html`,
                "_blank", "noopener,noreferrer"
              )}
            >Full Text</button>
            {selectedMD._center && (
              <button
                type="button"
                onClick={() => mapRef.current?.flyTo({ center: selectedMD._center, zoom: 6 })}
              >Center</button>
            )}
          </div>
        </div>
      )}

      {/* ── METAR popup (surface analysis — storm-chasing view) ── */}
      {selectedMetar && metarPopupPos && (
        <div className="sa-md-popup sa-metar-popup" style={{ left: metarPopupPos.left, top: metarPopupPos.top }}>
          <div className="sa-md-header">
            <span>{selectedMetar.id}</span>
            <button
              type="button"
              className="sa-warn-card-close"
              onClick={() => { setSelectedMetar(null); setMetarPopupPos(null); }}
              aria-label="Close"
            >
              <svg viewBox="0 0 12 12" fill="none" width="10" height="10" aria-hidden="true">
                <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          {selectedMetar.site && (
            <div className="sa-md-title" style={{ opacity: 0.6, fontSize: "11px" }}>{selectedMetar.site}</div>
          )}
          <div className="sa-md-body">
            {/* 1. Temp / Dewpoint / Spread — highest chase priority */}
            {(selectedMetar.temp != null || selectedMetar.dewp != null) && (
              <div className="sa-metar-row sa-metar-row--primary">
                {selectedMetar.temp != null && selectedMetar.dewp != null ? (
                  <>{selectedMetar.temp}° / {selectedMetar.dewp}°
                    <span className="sa-metar-spread"> · {Math.round(selectedMetar.temp - selectedMetar.dewp)}° spread</span>
                  </>
                ) : selectedMetar.temp != null
                  ? `${selectedMetar.temp}°C`
                  : `Dewp ${selectedMetar.dewp}°C`
                }
              </div>
            )}
            {/* 2. Wind — direction (cardinal + degrees), speed, gust */}
            {selectedMetar.wspd != null && (
              <div className="sa-metar-row">
                {selectedMetar.wdir === "VRB"
                  ? `VRB @ ${selectedMetar.wspd}kt`
                  : `${_cardinalDir(selectedMetar.wdir)} ${selectedMetar.wdir}° @ ${selectedMetar.wspd}kt`
                }
                {selectedMetar.wgst ? ` G${selectedMetar.wgst}kt` : ""}
              </div>
            )}
            {/* 3. Visibility */}
            {selectedMetar.visib != null && (
              <div className="sa-metar-row">Vis {selectedMetar.visib} SM</div>
            )}
            {/* 4. Altimeter — secondary */}
            {selectedMetar.altim != null && (
              <div className="sa-metar-row sa-metar-row--secondary">Alt {Number(selectedMetar.altim).toFixed(2)}"</div>
            )}
            {/* 5. Observation time */}
            {selectedMetar.obsTime && (
              <div className="sa-metar-row sa-metar-row--muted">
                {String(selectedMetar.obsTime).slice(11, 16)}Z
              </div>
            )}
            {/* 6. Raw METAR at bottom for experienced chasers */}
            {selectedMetar.rawOb && (
              <div className="sa-metar-raw">{selectedMetar.rawOb}</div>
            )}
          </div>
        </div>
      )}

      {/* ── Spotter / Report popup ── */}
      <SpotterPopup
        spotter={selectedSpotter}
        report={selectedReport}
        pos={spotterPopupPos}
        onClose={() => {
          setSelectedSpotter(null);
          setSelectedReport(null);
          setSpotterPopupPos(null);
        }}
      />

      {/* ── Mobile warning popup (all overlapping warnings, positioned near tap) ── */}
      {warnSheetOpen && selectedWarnings.length > 0 && warningPopupPos && (
        <div
          className="sa-warn-sheet"
          role="dialog"
          aria-label="Warning details"
          style={{ left: warningPopupPos.left, top: warningPopupPos.top }}
        >
          <div className="sa-warn-sheet__handle" />
          <div className="sa-warn-sheet__scroll">
            {selectedWarnings.map((w, i) => (
              <div key={w.id ?? i} className="sa-warn-card sa-warn-sheet__item">
                <div
                  className="sa-warn-card-header"
                  style={{
                    borderLeftColor: (
                      { tornado: "#f43f5e", storm: "#f59e0b", flood: "#3b82f6" }
                      [w.hazard_type] ?? "#94a3b8"
                    ),
                  }}
                >
                  <span className="sa-warn-card-event">
                    <span className="sa-warn-card-icon" aria-hidden="true">{getWarningIcon(w.hazard_type)}</span>
                    {w.event || "Warning"}
                  </span>
                  <button
                    type="button"
                    className="sa-warn-card-close"
                    onClick={() => { setWarnSheetOpen(false); setSelectedWarnings([]); }}
                    aria-label="Close warning details"
                  >
                    <svg viewBox="0 0 12 12" fill="none" width="10" height="10" aria-hidden="true">
                      <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                <div className="sa-warn-card-meta">
                  {(() => {
                    const { text, urgency } = getExpiresCountdown(w.expires_iso || w.expires, warningNow);
                    return (
                      <span className={`sa-warn-card-countdown sa-warn-card-countdown--${urgency}`}>
                        {text}
                      </span>
                    );
                  })()}
                  {w.obsType && w.obsType !== "Not specified" && (
                    <>
                      <span className="sa-warn-card-sep" aria-hidden="true">·</span>
                      <span className="sa-warn-card-obstype">{w.obsType}</span>
                    </>
                  )}
                </div>
                {w.areaDesc && (
                  <div className="sa-warn-card-area">{w.areaDesc}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <ReportSheet
        isOpen={isReportSheetOpen}
        onClose={() => setIsReportSheetOpen(false)}
        onSubmit={(report) => {
          console.log("Report submitted:", report);
          setIsReportSheetOpen(false);
        }}
      />

    </section>
  );
}
